import { defineMessage, defineMessages } from '@formatjs/intl';
import {
  restApiActivityDetails,
  restApiActivityOverview,
} from './TokenizedApiModelEntities';
import * as schema from '../utils/schema';
import { flattenFormattedParts, urlPath } from '../utils/utils';
import { CURRENCY_BSV, Instrument } from '../utils/instruments';
import { allowToFail } from '../utils/errors';
import Service, { minutes, seconds } from './Service';

const LOG_INCONSISTENT_DATEMODIFIED_STATS = false;
const SHOW_MULTIPLE_SIGNATURE_ROUNDS_REQUIRED = false;

const activityIdsDateModifiedInconsistent = new Map();
function updateDateModifiedStats(activityId, dateModifiedInconsistent) {
  if (
    !activityIdsDateModifiedInconsistent.has(activityId) ||
    (!activityIdsDateModifiedInconsistent.get(activityId) &&
      dateModifiedInconsistent)
  ) {
    activityIdsDateModifiedInconsistent.set(
      activityId,
      dateModifiedInconsistent,
    );
    console.log(
      `${
        [...activityIdsDateModifiedInconsistent.values()].filter(Boolean).length
      } of ${
        activityIdsDateModifiedInconsistent.size
      } activity events have inconsistent dateModified`,
    );
  }
}

// Set this to Date.now() to only see activity items after this point:
const START_TIME = 0;

/** @category SDK Modules */
export default class Activity extends Service {
  /** @internal */
  constructor(tokenizedApi) {
    super(tokenizedApi);
    this.cache = window.caches ? caches.open('network') : null;
    this.sseListeners = new Map();
  }

  activityOverviewQuery = this.makeQuery(
    (query, { profileId } = {}) => ({
      ...query,
      // SSE events should ensure activity is up-to-date,
      // so we don’t need regular refetching, but since the
      // overview query is cheap, we can re-run it whenever
      // the caller is remounted just in case
      refetchOnMount: 'always',
      // Avoid reloading delay when switching back to
      // stale data that would normally be purged
      cacheTime: Infinity,
      // If any of the most recent 10 events are in progress
      // (and active in the last 10 mins), actively refetch
      // the overview list every 5 seconds – this is to work
      // around missed activity updates due to dropped SSE events
      refetchInterval: (data) =>
        data
          ?.slice?.(0, 10)
          .some(
            (event) =>
              event.stage &&
              event.stage !== 'executed' &&
              !event.terminationReason &&
              event.dateModified &&
              event.dateModified > Date.now() - minutes(10),
          )
          ? seconds(5)
          : false,
      enabled: query.enabled && !!profileId,
      notifyOnChangeProps: ['data'],
    }),
    async (context, { profileId } = {}) => {
      const response = await this.tokenizedApi.endpoints.get(
        urlPath('profiles', profileId, 'activity', 'overview', 'no_lock_box'),
      );

      return schema
        .normalizeResponse(
          'Query activity overview',
          response.data.data,
          schema.arrayOf(restApiActivityOverview),
        )
        .normalizedValue.filter(({ dateCreated }) => dateCreated > START_TIME);
    },
  );

  activityEventQuery = this.makeQuery(
    (query, { profileId, lockboxId, activityId, dateModified } = {}) => ({
      ...query,
      // Activity events are normally updated by SSE and are cached in
      // persistent local storage.
      // Cached data will only be used when dateModified is supplied and the same
      // as the cached value.
      // Activity events with a dateModified are used in the activity table
      // and are updated when the overview query updates
      // Activity events without dateModified are used in the activity details
      staleTime: minutes(1),
      refetchInterval: (event) =>
        event?.stage &&
        event.stage !== 'executed' &&
        !event.terminationReason &&
        event.dateModified &&
        event.dateModified > Date.now() - minutes(10)
          ? seconds(5)
          : minutes(1),
      // Avoid reloading delay when switching back to
      // stale data that would normally be purged
      cacheTime: Infinity,
      enabled: query.enabled && !!profileId && !!activityId,
      notifyOnChangeProps: ['data', 'error'],
    }),
    async (
      context,
      { profileId, lockboxId, activityId, dateModified } = {},
    ) => {
      if (!profileId || !activityId) {
        return null;
      }

      const path = urlPath('profiles', profileId, 'activity', activityId);

      const key = new URL(path, this.tokenizedApi.endpoints.baseURL);

      const cache = await this.cache;
      const cachedResponse = await cache.match(key);
      let event;
      try {
        event = await cachedResponse?.clone().json();
      } catch (error) {
        console.error(
          `Error reading activity cache entry for ${activityId} – ignoring`,
          error,
        );
      }

      let dateModifiedInconsistent = false;
      if (!dateModified || event?.dateModified !== dateModified) {
        event = schema.normalizeResponse(
          'Query activity event',
          (await this.tokenizedApi.endpoints.get(path)).data.data,
          restApiActivityDetails,
        ).normalizedValue;
        const refetchedDateModified = event?.dateModified;

        if (dateModified && refetchedDateModified !== dateModified) {
          dateModifiedInconsistent = true;
          console.error(
            `Activity event ${activityId} (${event?.activityEventType}, ${event?.stage}, ${event?.terminationReason}) has inconsistent dateModified in overview query`,
          );
        }

        await cache.put(key, new Response(JSON.stringify(event)));
      }
      if (LOG_INCONSISTENT_DATEMODIFIED_STATS) {
        if (dateModified) {
          updateDateModifiedStats(activityId, dateModifiedInconsistent);
        }
      }

      const profiles = await allowToFail(
        () => this.tokenizedApi.entities.profilesQuery.fetch(),
        'activity event getting profiles',
      );
      const lockboxes = await allowToFail(
        () => this.tokenizedApi.treasury.lockboxesQuery.fetch(),
        'activity event getting lockboxes',
      );
      const entity = await allowToFail(
        () =>
          this.tokenizedApi.entities.entityQuery.fetch(
            profileId,
            profiles?.idMap?.get?.(profileId)?.entityId,
          ),
        'activity event getting entity',
      );

      return this.transformEvent(event, {
        profileId,
        entityId: entity?.id,
        isPersonalProfile: profiles?.personalProfile?.id === profileId,
        ownHandle: profiles?.personalProfile?.handle,
        lockboxes,
      });
    },
  );

  /**
   * Because of the way we use the activity event’s date-modified as part
   * of the cache key, we can be in a situation where null activity event
   * data is being returned by a particular query, but we do actually have
   * older data in the cache (with a different date-modified, or none).
   * This synchronous method will search the cache for a particular activity ID,
   * and return the latest available data, which can be used as a fallback
   * until the latest fetch completes.
   */
  getLatestFetchedActivityEvent({ profileId, lockboxId, activityId }) {
    const queryClient = this.tokenizedApi.getQueryClient();
    const allActivityVersions = queryClient.getQueriesData(
      this.activityEventQuery.makeCacheKey({
        profileId,
        lockboxId,
        activityId,
      }),
    );
    const inOrder = (allActivityVersions || [])
      .map(([_, event]) => event)
      .filter((event) => event?.dateModified)
      .sort((a, b) => b.dateModified - a.dateModified);
    return inOrder[0];
  }

  filterActivity(
    activity,
    {
      includeSuccessfulEvents = true,
      includeFailedEvents = true,
      includeEventsRequiringAction = true,
      includeEventsPendingOthers = true,
      includeUnrecognizedEvents = false,
      includeEventsForInstrument,
    } = {},
  ) {
    // Activity event data used to filter
    //   activityEventType,
    //   stage,
    //   terminationReason,
    //   !!requiresAction,
    //   !!pendingOthers,
    //   counterparties[].transfers[].instrumentId,
    //   contract.contractAddress,
    //   instruments[].instrumentId,

    const result = activity
      .filter(Boolean)
      .filter(
        ({
          activityEventType,
          stage,
          terminationReason,
          requiresAction,
          pendingOthers,
          counterparties,
          contract,
          instruments,
        }) => {
          switch (activityEventType) {
            // These are the only activity event types we show in activity lists,
            // so we want to filter out anything else, including 'dust' payments.
            case 'lock_box_creation_proposal':
            case 'payment':
            case 'transfer_fee':
            case 'trade_offer':
            case 'contract_formation':
            case 'contract_amendment':
            case 'contract_expire':
            case 'instrument_expire':
            case 'reorg':
              break;
            default:
              return false;
          }

          let includedByStatus;
          switch (terminationReason) {
            case 'revoked':
            case 'rejected':
            case 'cancelled':
            case 'failed':
            case 'expired':
              includedByStatus = !!includeFailedEvents;
              break;
            default:
              if (terminationReason) {
                // Unrecognized termination reason
                includedByStatus = !!includeUnrecognizedEvents;
              } else {
                switch (stage) {
                  // 'creating':
                  //     A new activity being reviewed by you before submitting
                  // 'connecting':
                  //     Waiting for the counterparty to respond with details
                  // 'confirming':
                  //     Waiting for you to initially confirm in the mobile app
                  // 'proposal':
                  // 'reviewing':
                  //     For multisig entities, a decision can be required
                  //     from you, or you can be waiting for other admins,
                  //     depending on the signing state
                  // 'accepting':
                  // 'finalizing':
                  //     Waiting for completion by the counterparty, or
                  //     for the smart contract agent to perform the action
                  case 'creating':
                  case 'connecting':
                  case 'confirming':
                  case 'proposal':
                  case 'reviewing':
                  case 'accepting':
                  case 'finalizing':
                    // For all known “in-progress” stages, we follow the categorization
                    // (requiresAction, pendingOthers) supplied by the transform method
                    if (requiresAction) {
                      includedByStatus = !!includeEventsRequiringAction;
                    } else if (pendingOthers) {
                      includedByStatus = !!includeEventsPendingOthers;
                    } else {
                      includedByStatus = !!includeUnrecognizedEvents;
                    }
                    break;
                  case 'executed':
                    includedByStatus = !!includeSuccessfulEvents;
                    break;
                  default:
                    includedByStatus = !!includeUnrecognizedEvents;
                    break;
                }
              }
              break;
          }
          if (includeEventsForInstrument?.instrumentId) {
            if (!includedByStatus) {
              return false;
            }
            // Check if this activity transfers the instrument
            if (
              counterparties?.some?.((party) =>
                party.transfers?.some?.((transfer) => {
                  if (
                    transfer.instrumentId &&
                    transfer.instrumentId ===
                      includeEventsForInstrument.instrumentId
                  ) {
                    return true;
                  }
                }),
              )
            ) {
              return true;
            }
            // Check if this activity modifies the instrument
            if (
              instruments?.some?.((instrument) => {
                if (
                  instrument.instrumentId &&
                  instrument.instrumentId ===
                    includeEventsForInstrument.instrumentId
                ) {
                  return true;
                }
              })
            ) {
              return true;
            }
            // Check if this activity modifies the instrument’s contract
            if (
              includeEventsForInstrument.contractAddress &&
              includeEventsForInstrument.contractAddress ===
                contract?.contractAddress
            ) {
              return true;
            }
            return false;
          }
          return includedByStatus;
        },
      );
    return result;
  }

  filterPendingWorkspaces(activity) {
    return activity.filter(
      ({
        activityEventType,
        stage,
        reorgStage,
        isEventForThisEntity,
        terminationReason,
      }) =>
        (activityEventType === 'lock_box_creation_proposal' ||
          (activityEventType === 'reorg' &&
            reorgStage === 'lockbox_creation')) &&
        (stage === 'proposal' || terminationReason) &&
        !isEventForThisEntity,
    );
  }

  /** @internal */
  onNewActivity(event) {
    let data;
    if (event?.data) {
      try {
        data = JSON.parse(event.data);
        if (window.enableSSELogging) {
          console.log(`🛜 SSE: activity event: ${event.data}`);
        }
      } catch (error) {
        console.error('Unable to parse activity event data', error);
      }
    }

    let {
      profile_id: profileId,
      id: activityId,
      update_type: updateType,
    } = data;

    if (updateType == 'lockbox') {
      // Change of lockboxes should result in all queries being reloaded:
      this.tokenizedApi.query.reloadAll();
      // Enable this to see the SSE event in the network debugger:
      // fetch('data:text/html,reload everything');
    }

    if (profileId) {
      if (activityId) {
        this.activityEventQuery.invalidate({
          profileId,
          activityId,
        });
      } else {
        this.activityEventQuery.invalidate({ profileId });
      }
      this.activityOverviewQuery.invalidate({ profileId });
    } else {
      // Update all profiles if we don’t know which one it was
      if (activityId) {
        this.activityEventQuery.invalidate({ activityId });
      } else {
        this.activityEventQuery.invalidate();
      }
      this.activityOverviewQuery.invalidate();
    }
    this.tokenizedApi.treasury.onActivity(profileId, activityId);
    this.tokenizedApi.contracts.onActivity(profileId, activityId);
  }

  /** @internal */
  startListening() {
    this.stopListening();
    this.isListening = true;
    this.connectToSSE();
  }

  addSSEListener(type, callback) {
    if (!this.sseListeners.has(type)) this.sseListeners.set(type, new Set());
    this.sseListeners.get(type).add(callback);
    this.eventSource?.addEventListener(type, callback);
    return () => {
      this.sseListeners.get(type).delete(callback);
      this.eventSource?.removeEventListener(type, callback);
    };
  }

  /** @internal */
  async connectToSSE() {
    if (
      !this.isListening ||
      this.eventSource ||
      !this.tokenizedApi.config.listenForActivity ||
      // SSE not available for local installations unless configured
      !this.tokenizedApi.endpoints.sse
    ) {
      return;
    }
    try {
      const tokenResponse = await this.tokenizedApi.endpoints.postSSE(
        urlPath('sse', 'token'),
      );
      const token = tokenResponse?.data?.data?.hash;
      if (token) {
        this.eventSource = new EventSource(
          `${this.tokenizedApi.endpoints.getSSEBaseUrl()}/sse?accessKey=${encodeURIComponent(
            token,
          )}`,
          {},
        );
        if (window.enableSSELogging) {
          console.log(
            `🛜 SSE: connected to ${this.tokenizedApi.endpoints.getSSEBaseUrl()}/sse`,
          );
        }
        this.eventSource.onerror = () => {
          this.eventSource?.close?.();
          this.eventSource = null;
          if (window.enableSSELogging) {
            console.log('🛜 SSE: error, reconnecting in 5 seconds');
          }
          this.retrySSE = setTimeout(() => this.connectToSSE(), 5000);
        };
        this.eventSource.addEventListener('activity', (event) =>
          this.onNewActivity(event),
        );
        for (let [type, callbacks] of this.sseListeners) {
          for (let callback of callbacks) {
            this.eventSource.addEventListener(type, callback);
          }
        }
      }
    } catch (error) {
      console.error(error);
      this.eventSource?.close?.();
      this.eventSource = null;
    }
    if (!this.eventSource) {
      this.retrySSE = setTimeout(() => this.connectToSSE(), 5000);
    }
  }

  /** @internal */
  stopListening() {
    this.isListening = false;
    if (this.retrySSE) {
      clearTimeout(this.retrySSE);
      this.retrySSE = null;
    }
    this.eventSource?.close?.();
    this.eventSource = null;
  }

  /** @internal */
  makeFormatEntityDescription(entity, verb) {
    return (intl, outputParts) => {
      const isAnonymous =
        entity?.isAnonymous ||
        (!entity?.displayName &&
          !entity?.displayHandle &&
          !entity?.isInternal &&
          !entity?.isVerified);

      let isVerified = entity?.isVerified || !entity?.displayHandle;
      let name = entity?.displayName || entity?.displayHandle;
      // Special case for new account BSV funding transfer
      if (entity?.displayHandle === 'teller@' && !entity?.displayName) {
        isVerified = true;
        name = intl.formatMessage({
          defaultMessage: 'Tokenized Pty Ltd',
          description: 'Sender name for new account BSV funding transfers',
          id: 'TokenizedSDK:contact.name.funding',
        });
      }

      let messageDescriptor;
      if (isAnonymous) {
        switch (verb) {
          case 'to':
            messageDescriptor = defineMessage({
              defaultMessage: '<verb>to</verb> <entity>a BSV address</entity>',
              description:
                'Activity event counterparty (shown after/below description)',
              id: 'TokenizedSDK:activity.counterparty.anonymous.to',
            });
            break;
          case 'from':
            messageDescriptor = defineMessage({
              defaultMessage:
                '<verb>from</verb> <entity>a BSV address</entity>',
              description:
                'Activity event counterparty (shown after/below description)',
              id: 'TokenizedSDK:activity.counterparty.anonymous.from',
            });
            break;
          case 'with':
            messageDescriptor = defineMessage({
              defaultMessage:
                '<verb>with</verb> <entity>a BSV address</entity>',
              description:
                'Activity event counterparty (shown after/below description)',
              id: 'TokenizedSDK:activity.counterparty.anonymous.with',
            });
            break;
          case 'by':
            messageDescriptor = defineMessage({
              defaultMessage: '<verb>by</verb> <entity>a BSV address</entity>',
              description:
                'Activity event counterparty (shown after/below description)',
              id: 'TokenizedSDK:activity.counterparty.anonymous.by',
            });
            break;
          case 'for':
            messageDescriptor = defineMessage({
              defaultMessage: '<verb>for</verb> <entity>a BSV address</entity>',
              description:
                'Activity event counterparty (shown after/below description)',
              id: 'TokenizedSDK:activity.counterparty.anonymous.for',
            });
            break;
          default:
            messageDescriptor = defineMessage({
              defaultMessage: '<entity>a BSV address</entity>',
              description:
                'Activity event counterparty (shown after/below description)',
              id: 'TokenizedSDK:activity.counterparty.anonymous',
            });
            break;
        }
      } else {
        switch (verb) {
          case 'to':
            if (isVerified) {
              messageDescriptor = defineMessage({
                defaultMessage: '<verb>to</verb> <entity>{name}</entity>',
                description:
                  'Activity event counterparty (shown after/below description)',
                id: 'TokenizedSDK:activity.counterparty.to',
              });
            } else {
              messageDescriptor = defineMessage({
                defaultMessage: '<verb>to</verb> <entity>{name}</entity>',
                description:
                  'Activity event counterparty (shown after/below description)',
                id: 'TokenizedSDK:activity.counterparty.unverified.to',
              });
            }
            break;
          case 'from':
            if (isVerified) {
              messageDescriptor = defineMessage({
                defaultMessage: '<verb>from</verb> <entity>{name}</entity>',
                description:
                  'Activity event counterparty (shown after/below description)',
                id: 'TokenizedSDK:activity.counterparty.from',
              });
            } else {
              messageDescriptor = defineMessage({
                defaultMessage: '<verb>from</verb> <entity>{name}</entity>',
                description:
                  'Activity event counterparty (shown after/below description)',
                id: 'TokenizedSDK:activity.counterparty.unverified.from',
              });
            }
            break;
          case 'with':
            if (isVerified) {
              messageDescriptor = defineMessage({
                defaultMessage: '<verb>with</verb> <entity>{name}</entity>',
                description:
                  'Activity event counterparty (shown after/below description)',
                id: 'TokenizedSDK:activity.counterparty.with',
              });
            } else {
              messageDescriptor = defineMessage({
                defaultMessage: '<verb>with</verb> <entity>{name}</entity>',
                description:
                  'Activity event counterparty (shown after/below description)',
                id: 'TokenizedSDK:activity.counterparty.unverified.with',
              });
            }
            break;
          case 'by':
            if (isVerified) {
              messageDescriptor = defineMessage({
                defaultMessage: '<verb>by</verb> <entity>{name}</entity>',
                description:
                  'Activity event counterparty (shown after/below description)',
                id: 'TokenizedSDK:activity.counterparty.by',
              });
            } else {
              messageDescriptor = defineMessage({
                defaultMessage: '<verb>by</verb> <entity>{name}</entity>',
                description:
                  'Activity event counterparty (shown after/below description)',
                id: 'TokenizedSDK:activity.counterparty.unverified.by',
              });
            }
            break;
          case 'for':
            if (isVerified) {
              messageDescriptor = defineMessage({
                defaultMessage: '<verb>for</verb> <entity>{name}</entity>',
                description:
                  'Activity event counterparty (shown after/below description)',
                id: 'TokenizedSDK:activity.counterparty.for',
              });
            } else {
              messageDescriptor = defineMessage({
                defaultMessage: '<verb>for</verb> <entity>{name}</entity>',
                description:
                  'Activity event counterparty (shown after/below description)',
                id: 'TokenizedSDK:activity.counterparty.unverified.for',
              });
            }
            break;
          default:
            if (isVerified) {
              messageDescriptor = defineMessage({
                defaultMessage: '<entity>{name}</entity>',
                description:
                  'Activity event counterparty (shown after/below description)',
                id: 'TokenizedSDK:activity.counterparty',
              });
            } else {
              messageDescriptor = defineMessage({
                defaultMessage: '<entity>{name}</entity>',
                description:
                  'Activity event counterparty (shown after/below description)',
                id: 'TokenizedSDK:activity.counterparty.unverified',
              });
            }
            break;
        }
      }

      if (outputParts) {
        const formatResult = intl.formatMessage(messageDescriptor, {
          name,
          verb: (formatted) => ({
            type: 'verb',
            value: flattenFormattedParts(formatted),
          }),
          entity: (formatted) => ({
            type: 'entity',
            ...(entity?.isInternal ? { handle: entity?.displayHandle } : {}),
            value: flattenFormattedParts(formatted),
          }),
          status: (formatted) => ({
            type: 'status',
            value: flattenFormattedParts(formatted),
          }),
        });
        if (Array.isArray(formatResult)) {
          return formatResult.map((part) =>
            typeof part === 'string' ? { type: 'literal', value: part } : part,
          );
        }
        return [formatResult];
      }
      return intl.formatMessage(messageDescriptor, {
        name,
        verb: (formatted) => formatted,
        entity: (formatted) => formatted,
        status: (formatted) => formatted,
      });
    };
  }

  /** @internal */
  makeFormatTransferDescription(transfer, verb) {
    return (intl, instrument, outputParts) => {
      if (!instrument?.hasDetails && !transfer?.nameFromDraft) {
        return;
      }
      let messageDescriptor;
      switch (verb) {
        case 'sent':
          messageDescriptor = defineMessage({
            defaultMessage: '<verb>sent</verb> <name>{instrument}</name>',
            description: 'Activity event transfer description',
            id: 'TokenizedSDK:activity.transfer.sent',
          });
          break;
        case 'toSend':
          messageDescriptor = defineMessage({
            defaultMessage: '<verb>to send</verb> <name>{instrument}</name>',
            description: 'Activity event transfer description',
            id: 'TokenizedSDK:activity.transfer.toSend',
          });
          break;
        case 'offered':
          messageDescriptor = defineMessage({
            defaultMessage: '<verb>offered</verb> <name>{instrument}</name>',
            description: 'Activity event transfer description',
            id: 'TokenizedSDK:activity.transfer.offered',
          });
          break;
        case 'for':
          messageDescriptor = defineMessage({
            defaultMessage: '<verb>for</verb> <name>{instrument}</name>',
            description: 'Activity event transfer description',
            id: 'TokenizedSDK:activity.transfer.for',
          });
          break;
        case 'forTransfer':
          messageDescriptor = defineMessage({
            defaultMessage:
              '<verb>for a transfer of</verb> <name>{instrument}</name>',
            description: 'Activity event transfer fee instrument description',
            id: 'TokenizedSDK:activity.transferFee.instrument',
          });
          break;
        case 'received':
          messageDescriptor = defineMessage({
            defaultMessage: '<verb>received</verb> <name>{instrument}</name>',
            description: 'Activity event transfer description',
            id: 'TokenizedSDK:activity.transfer.received',
          });
          break;
        case 'toReceive':
          messageDescriptor = defineMessage({
            defaultMessage: '<verb>to receive</verb> <name>{instrument}</name>',
            description: 'Activity event transfer description',
            id: 'TokenizedSDK:activity.transfer.toReceive',
          });
          break;
        case 'offering':
          messageDescriptor = defineMessage({
            defaultMessage: '<verb>offering</verb> <name>{instrument}</name>',
            description: 'Activity event transfer description',
            id: 'TokenizedSDK:activity.transfer.offering',
          });
          break;
        default:
          messageDescriptor = defineMessage({
            defaultMessage: '<name>{instrument}</name>',
            description: 'Activity event transfer description',
            id: 'TokenizedSDK:activity.transfer',
          });
          break;
      }
      if (outputParts) {
        const formatResult = intl.formatMessage(messageDescriptor, {
          instrument:
            transfer?.nameFromDraft || instrument.formatInstrumentName(),
          verb: (formatted) => ({
            type: 'verb',
            value: flattenFormattedParts(formatted),
          }),
          name: (formatted) => ({
            type: 'instrument',
            instrumentId: instrument?.instrumentId,
            value: flattenFormattedParts(formatted),
          }),
        });
        if (Array.isArray(formatResult)) {
          return formatResult.map((part) =>
            typeof part === 'string' ? { type: 'literal', value: part } : part,
          );
        }
        return [formatResult];
      }
      return intl.formatMessage(messageDescriptor, {
        instrument:
          transfer?.nameFromDraft || instrument.formatInstrumentName(),
        verb: (formatted) => formatted,
        name: (formatted) => formatted,
      });
    };
  }

  /** @internal */
  makeFormatControlArrangement(lockbox, isIndividual) {
    const numberOfAdministrators = lockbox?.signers?.length || 1;
    const requiredSigners = lockbox?.requiredSigners || 1;

    let formatControlArrangement;
    if (isIndividual) {
      formatControlArrangement = (intl) =>
        intl.formatMessage({
          id: 'TokenizedSDK:controlArrangement.individual',
          defaultMessage: 'Account holder',
          description:
            'Workspace control arrangement description (personal workspace)',
        });
    } else {
      if (numberOfAdministrators <= 1) {
        formatControlArrangement = (intl) =>
          intl.formatMessage({
            id: 'TokenizedSDK:controlArrangement.soleAdmin',
            defaultMessage: '1 admin',
            description:
              'Workspace control arrangement description (only 1 administrator)',
          });
      } else {
        formatControlArrangement = (intl) =>
          intl.formatMessage(
            {
              id: 'TokenizedSDK:controlArrangement.multipleAdmins',
              defaultMessage: '{required, number} of {total, number} control',
              description:
                'Workspace control arrangement description (2 or more administrators)',
            },
            {
              required: requiredSigners,
              total: numberOfAdministrators,
            },
          );
      }
    }

    return formatControlArrangement;
  }

  /** @internal */
  makeSignProposalCallback(profileId, transaction) {
    if (profileId && transaction?.pendingTransactionId) {
      return async (setMfaRequest) =>
        await this.tokenizedApi.transfers.signProposal({
          profileId,
          pendingTransactionId: transaction.pendingTransactionId,
          setMfaRequest,
        });
    }
  }

  /** @internal */
  makeResumeConfirmCallback(profileId, event) {
    const allPendingTransactions = (event?.transactions || []).filter(
      (transaction) => !transaction.dateComplete && !transaction.dateCancelled,
    );
    const firstPendingTransaction = allPendingTransactions[0];

    if (profileId && firstPendingTransaction) {
      return this.makeSignProposalCallback(profileId, firstPendingTransaction);
    }
  }

  /** @internal */
  makeApproveCallback(profileId, event) {
    if (profileId && event?.lockboxId && event?.id) {
      return async (setMfaRequest) =>
        await this.tokenizedApi.transfers.approve({
          profileId,
          lockboxId: event.lockboxId,
          activityId: event.id,
          setMfaRequest,
        });
    }
  }

  /** @internal */
  makeRejectCallback(profileId, event) {
    if (profileId && event?.lockboxId && event?.id) {
      return async (setMfaRequest) =>
        await this.tokenizedApi.transfers.reject({
          profileId,
          lockboxId: event.lockboxId,
          activityId: event.id,
          setMfaRequest,
        });
    }
  }

  /** @internal */
  makeRejectProposalCallback(profileId, transaction) {
    if (profileId && transaction?.requestId) {
      return async (setMfaRequest) =>
        await this.tokenizedApi.transfers.rejectProposal({
          profileId,
          requestId: transaction.requestId,
          setMfaRequest,
        });
    }
  }

  /** @internal */
  makeCancelProposalCallback(profileId, event) {
    return async () =>
      await this.tokenizedApi.transfers.cancelActivity({
        profileId,
        activityId: event.id,
      });
  }

  /** @internal */
  calculateMultisigState(event, { profileId, lockboxes, ownHandle }) {
    const allLockboxIds = new Set();
    if (event?.lockboxId) {
      allLockboxIds.add(event.lockboxId);
    }
    (event?.counterparties || []).forEach((counterparty) => {
      if (counterparty.povLockboxId) {
        allLockboxIds.add(counterparty.povLockboxId);
      }
    });

    const lockbox = lockboxes?.idMap?.get(
      event?.lockboxId || event?.counterparties?.[0]?.povLockboxId,
    );

    // TODO: refine
    // There is no longer a single lockbox for an activity in the case of a re-organisation.
    // One activity can be both multi and single signer
    let isMultisig = false;
    for (const lockboxId of allLockboxIds) {
      const lockbox = lockboxes?.idMap?.get(lockboxId);
      if (lockbox?.requiredSigners > 1) {
        isMultisig = true;
        break;
      }
    }

    let result = {
      isMultisig: false,
    };

    if (isMultisig) {
      const signableTransactions = (event?.transactions || []).filter(
        ({ signers }) => signers?.length,
      );
      const allPendingTransactions = signableTransactions.filter(
        (transaction) =>
          !transaction.dateComplete && !transaction.dateCancelled,
      );
      const firstTransaction = signableTransactions[0];
      const firstPendingTransaction = allPendingTransactions[0];
      const nextTransaction = firstPendingTransaction;
      const noPendingTransactions = !firstPendingTransaction;

      const hasProposer =
        event?.stage === 'creating' ||
        event?.stage === 'connecting' ||
        event?.stage === 'confirming' ||
        event?.stage === 'proposal' ||
        !!event?.isInitiator;

      const amOriginalProposer =
        event?.stage === 'creating' ||
        event?.stage === 'confirming' ||
        (event?.stage === 'connecting' && !!event?.isInitiator) ||
        !!firstTransaction?.isProposer;
      const amProposer =
        event?.stage === 'creating' ||
        event?.stage === 'confirming' ||
        (event?.stage === 'connecting' && !!event?.isInitiator) ||
        (event?.stage === 'proposal' && !!nextTransaction?.isProposer);

      const signingStarted =
        !!event?.stage &&
        event.stage !== 'creating' &&
        event.stage !== 'connecting';
      const proposalSubmitted = signingStarted && event.stage !== 'confirming';

      const administratorCount = lockbox?.signers?.length || 1;

      let proposerHandle = null;
      let proposalTimestamp;
      if (hasProposer) {
        proposerHandle = amOriginalProposer
          ? ownHandle
          : firstTransaction?.signers?.find(({ isProposer }) => isProposer)
              ?.handle;
        proposalTimestamp = event?.dateCreated;
      }

      const amValidSignerForNextTransaction =
        noPendingTransactions ||
        nextTransaction?.signers?.some(({ handle }) => handle === ownHandle);

      // Default multisig state is an accepted signature round
      // that this entity didn’t participate in (just an arbitrary
      // safe choice in case the state is invalid)
      let stageRequiresSignatureRound = false;
      let haveAddedSignature = false;
      let undecidedSignatories = 0;
      let hasProposerActions = false;
      let hasCosignerActions = false;
      let signaturesRequired = lockbox?.requiredSigners || administratorCount;
      if (
        SHOW_MULTIPLE_SIGNATURE_ROUNDS_REQUIRED &&
        allPendingTransactions.length > 1
      ) {
        signaturesRequired = undefined;
      }
      let signaturesRemaining = 0;
      let rejectionCount;
      let signatureCount;
      let rejectionCountCurrentRound;
      let signatureCountCurrentRound;
      let rejectionsToBlock;
      let multisigState = 'review';
      let pendingOthers = false;
      let requiresAction = false;
      let signProposal, rejectProposal;

      haveAddedSignature =
        !!nextTransaction?.dateSigned || !!nextTransaction?.dateRejected;
      rejectionCount = 0;
      signatureCount = 0;
      rejectionCountCurrentRound = 0;
      signatureCountCurrentRound = 0;
      signableTransactions.forEach((pendingTransaction) => {
        pendingTransaction?.signers?.forEach((signer) => {
          if (signer.dateRejected) {
            rejectionCount += 1;
            if (pendingTransaction === nextTransaction) {
              rejectionCountCurrentRound += 1;
            }
          } else if (signer.dateSigned) {
            signatureCount += 1;
            if (pendingTransaction === nextTransaction) {
              signatureCountCurrentRound += 1;
            }
          }
        });
      });

      if (
        !event?.terminationReason &&
        (event?.stage === 'creating' ||
          event?.stage === 'connecting' ||
          event?.stage === 'confirming' ||
          event?.stage === 'proposal' ||
          (event?.stage === 'reviewing' && !event.isInitiator))
      ) {
        // Note: signatures are not actually required at the creating or confirming
        // stages, but we want to show the information about expected signatures in
        // the review/confirm dialog, so we calculate the info as if we reached 'proposal'
        stageRequiresSignatureRound = true;
        hasProposerActions = amProposer;
        hasCosignerActions = !amProposer && !haveAddedSignature;
      }

      if (
        !SHOW_MULTIPLE_SIGNATURE_ROUNDS_REQUIRED ||
        allPendingTransactions.length <= 1
      ) {
        signaturesRemaining = Math.max(
          0,
          signaturesRequired - signatureCountCurrentRound,
        );
        undecidedSignatories = Math.max(
          0,
          administratorCount -
            signatureCountCurrentRound -
            rejectionCountCurrentRound,
        );
        rejectionsToBlock = undecidedSignatories - signaturesRemaining + 1;
      } else {
        signaturesRemaining = undefined;
        undecidedSignatories = undefined;
        rejectionsToBlock = undefined;
      }

      if (event?.stage === 'reviewing') {
        // When responding to a counterparty’s offer, we’re in the complex
        // situation of having two levels of rejections to consider: there’s
        // each individual admin’s rejection, but also the underlying rejection of
        // the offer (i.e. the whole activity). Currently we don’t have a good
        // way to resolve the signatures required to represent a multisig offer
        // rejection on-chain (because this potentially requires the additional
        // signatures of admins who originally accepted it). Instead we choose a
        // simpler behavior for now: offer rejections at the reviewing stage are
        // unlilateral (any admin can reject the offer), and no on-chain rejection
        // message is recorded.
        rejectionsToBlock = 1;
      }

      if (stageRequiresSignatureRound) {
        if (haveAddedSignature || !amValidSignerForNextTransaction) {
          pendingOthers = true;
          if (!amValidSignerForNextTransaction) {
            multisigState = 'review';
          } else if (hasProposerActions && event?.stage === 'proposal') {
            // TODO: provide revokeProposal callback
            multisigState = 'proposed';
          } else {
            if (nextTransaction?.dateRejected) {
              multisigState = 'rejected';
            } else {
              multisigState = 'signed';
            }
          }
        } else {
          if (noPendingTransactions) {
            if (event?.stage !== 'proposal') {
              // The first administrator to sign at the reviewing or countersigning
              // stage of a trade has to call the accept/reject endpoint to create the
              // pending transaction
              signProposal = this.makeApproveCallback(profileId, event);
              rejectProposal = this.makeRejectCallback(profileId, event);
            }
          } else {
            signProposal = this.makeSignProposalCallback(
              profileId,
              nextTransaction,
            );
            if (event?.stage === 'reviewing') {
              // A multisig admin can unilaterally reject a trade offer
              // or instrument request at the reviewing stage (see above)
              rejectProposal = this.makeRejectCallback(profileId, event);
            } else {
              rejectProposal = this.makeRejectProposalCallback(
                profileId,
                nextTransaction,
              );
            }
          }
          requiresAction = true;
          multisigState = 'review';
        }
      }

      result = {
        isMultisig,
        amOriginalProposer,
        amProposer,
        proposerHandle,
        proposalTimestamp,
        pendingOthers,
        requiresAction,
        signingStarted,
        proposalSubmitted,
        multisigState,
        stageRequiresSignatureRound,
        noPendingTransactions,
        amValidSignerForNextTransaction,
        haveAddedSignature,
        administratorCount,
        undecidedSignatories,
        hasProposerActions,
        hasCosignerActions,
        signaturesRequired,
        signaturesRemaining,
        rejectionCount,
        signatureCount,
        rejectionCountCurrentRound,
        signatureCountCurrentRound,
        rejectionsToBlock,
        signProposal,
        rejectProposal,
      };
    }

    return result;
  }

  /** @internal */
  multisigTimelineMessages = defineMessages({
    failed: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.failed',
      defaultMessage: 'Proposal retracted',
    },
    succeeded: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.succeeded',
      defaultMessage: 'Executed',
    },
    next: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.next',
      defaultMessage: 'Executing',
    },
    blocked: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.blocked',
      defaultMessage: 'Proposal blocked',
    },
    expired: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.expired',
      defaultMessage: 'Proposal expired',
    },
    remainingUnknown: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.remainingUnknown',
      defaultMessage: 'Multiple approvals required',
    },
    remainingAll: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.remainingAll',
      defaultMessage:
        '{remaining, plural, one {1 approval required} other {# approvals required}}',
    },
    remaining: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.remaining',
      defaultMessage:
        '{remaining, plural, one {1 more approval required} other {# more approvals required}}',
    },
    remainingInvitations: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.remainingInvitations',
      defaultMessage:
        '{remaining, plural, one {1 pending invitation} other {# pending invitations}}',
    },
    invitations: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.invitations',
      defaultMessage:
        '{remaining, plural, one {Ready to send 1 invitation} other {Ready to send # invitations}}',
    },
    submitProposal: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.submitProposal',
      defaultMessage: 'Submit proposal',
    },
    proposed: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.proposed',
      defaultMessage: 'Proposed by {name}',
    },
    approved: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.signatures.approved',
      defaultMessage:
        'Approved by {approvals, plural, one {1 admin} other {# admins}}',
    },
    rejected: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.signatures.rejected',
      defaultMessage:
        'Rejected by {rejections, plural, one {1 admin} other {# admins}}',
    },
    collect: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.signatures.pending',
      defaultMessage:
        'Collect approval from {remaining, plural, one {1 admin} other {# admins}}',
    },
    startCollecting: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.signatures.ready',
      defaultMessage:
        'Collecting approval from {remaining, plural, one {1 admin} other {# admins}}',
    },
    continueCollecting: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.signatures.remaining',
      defaultMessage:
        'Collecting approval from {remaining, plural, one {1 more admin} other {# more admins}}',
    },
    collected: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.signatures.partial',
      defaultMessage:
        'Collected approval from {approvals, plural, one {1 admin} other {# admins}}',
    },
    collectedNone: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.signatures.none',
      defaultMessage: 'No admin approvals collected',
    },
    unknownCollecting: {
      id: 'TokenizedSDK:ActivityEvent.timeline.multisig.signatures.more',
      defaultMessage: 'Collecting approval from more admins',
    },
  });

  /** @internal */
  makeTimelineForTransactions({
    transactions,
    workspaceInvitationProgress,
    multisigState,
    nextEvents,
    conclusionEvent,
    conclusionMessage,
    conclusionTimestamp,
    isProcessing,
    insertBeforeConclusion,
    previousEvents,
    multisigFallbackTransaction,
  }) {
    const messages = {
      ...this.multisigTimelineMessages,
      ...(conclusionEvent && conclusionMessage
        ? { [conclusionEvent]: conclusionMessage }
        : {}),
    };

    const isWorkspaceInvitation = !!workspaceInvitationProgress;
    if (isWorkspaceInvitation) {
      const latestDate = workspaceInvitationProgress.reduce(
        (latest, signer) =>
          signer?.dateAccepted || signer?.dateRejected
            ? Math.max(latest, signer?.dateAccepted || signer?.dateRejected)
            : latest,
        0,
      );
      const isRejected = workspaceInvitationProgress.some(
        (signer) => signer?.dateRejected,
      );
      const isApproved = workspaceInvitationProgress.every(
        (signer) => signer?.dateAccepted,
      );
      transactions = [
        {
          dateCancelled: isRejected ? latestDate : undefined,
          dateComplete: isApproved && latestDate ? latestDate : undefined,
          signers: workspaceInvitationProgress.map((workspaceSigner) => ({
            ...workspaceSigner,
            dateSigned: workspaceSigner.dateAccepted,
          })),
        },
      ];
    }

    const txId = transactions?.length
      ? transactions[transactions.length - 1].txId
      : null;

    if (conclusionEvent === 'revoked') {
      conclusionEvent = 'failed';
    }

    let timeline = [];
    if (conclusionEvent) {
      if (conclusionEvent === 'blocked') {
        timeline = [
          {
            status: 'failed',
            formatDescription: ({ intl }) =>
              intl.formatMessage(messages.blocked),
          },
        ];
      } else {
        timeline = [
          {
            status: conclusionEvent,
            formatDescription: ({ intl }) =>
              intl.formatMessage(messages[conclusionEvent]),
            txId,
            isProcessing,
            timestamp: conclusionTimestamp,
          },
        ];
      }
    }

    if (nextEvents) {
      timeline = [
        ...timeline,
        ...nextEvents.map((event) => ({
          status: 'pending',
          ...event,
        })),
      ];
    }

    if (insertBeforeConclusion) {
      timeline = [...insertBeforeConclusion, ...timeline];
    }

    if (!multisigState?.isMultisig) {
      if (previousEvents) {
        timeline = [
          ...previousEvents.map((event) => ({
            status: 'completed',
            ...event,
          })),
          ...timeline,
        ];
      }
      return timeline;
    }

    let proposalEvent = [];
    if (multisigState.proposerHandle) {
      proposalEvent = [
        {
          status: multisigState.proposalSubmitted ? 'completed' : 'progress',
          handle: multisigState.proposerHandle,
          formatDescription: multisigState.proposalSubmitted
            ? ({ intl, name }) =>
                intl.formatMessage(messages.proposed, {
                  name,
                })
            : ({ intl }) => intl.formatMessage(messages.submitProposal),
          timestamp: multisigState.proposalTimestamp,
        },
      ];
    }

    let showNextTransactionInProgress = multisigState.proposalSubmitted;
    let signingRoundTransactions = (transactions || []).filter(
      ({ signers }) => signers?.length,
    );
    if (!signingRoundTransactions.length && multisigFallbackTransaction) {
      signingRoundTransactions = [multisigFallbackTransaction];
    }
    const signingRounds = signingRoundTransactions
      .map((transaction) => ({
        ...transaction,
        lastModified:
          transaction.dateComplete ||
          transaction.dateCancelled ||
          transaction.signers.reduce((lastModified, signer) => {
            const responseTimestamp = signer.dateSigned || signer.dateRejected;
            return responseTimestamp &&
              (!lastModified || responseTimestamp > lastModified)
              ? responseTimestamp
              : lastModified;
          }, null),
      }))
      .sort((a, b) => {
        const timeA = a.lastModified;
        const timeB = b.lastModified;
        const completeA = !!a.dateComplete || !!a.dateCancelled;
        const completeB = !!b.dateComplete || !!b.dateCancelled;
        const inProgressA = !!timeA && !completeA;
        const inProgressB = !!timeB && !completeB;
        const notStartedA = !timeA;
        const notStartedB = !timeB;
        const blockA = (inProgressA ? 1 : 0) + (notStartedA ? 2 : 0);
        const blockB = (inProgressB ? 1 : 0) + (notStartedB ? 2 : 0);
        if (blockA !== blockB) {
          return blockA - blockB;
        }
        return blockA < 2 ? timeA - timeB : 0;
      })
      .map((transaction, transactionIndex) => {
        let status = 'progress';
        let progressFraction = 0;
        let formatDescription;

        const numberOfSignatures = transaction.signers.reduce(
          (count, signer) => count + (signer.dateSigned ? 1 : 0),
          0,
        );

        if (transaction.dateComplete) {
          status = 'signed';
          formatDescription = ({ intl }) =>
            intl.formatMessage(messages.approved, {
              approvals: numberOfSignatures,
            });
        } else {
          const signaturesRequired =
            (isWorkspaceInvitation
              ? transaction.signers.length
              : multisigState.signaturesRequired) || 1;
          progressFraction =
            Math.min(numberOfSignatures, signaturesRequired - 1) /
            signaturesRequired;
          if (transaction.dateCancelled) {
            if (numberOfSignatures <= 0) {
              formatDescription = ({ intl }) =>
                intl.formatMessage(messages.collectedNone);
            } else {
              formatDescription = ({ intl }) =>
                intl.formatMessage(messages.collected, {
                  approvals: numberOfSignatures,
                });
            }
          } else if (!progressFraction && !showNextTransactionInProgress) {
            formatDescription = ({ intl }) =>
              intl.formatMessage(messages.collect, {
                remaining: signaturesRequired,
              });
            status = 'pending';
          } else {
            if (numberOfSignatures === 0) {
              formatDescription = ({ intl }) =>
                intl.formatMessage(messages.startCollecting, {
                  remaining: signaturesRequired,
                });
            } else {
              const remainingSignatures =
                signaturesRequired - numberOfSignatures;
              if (remainingSignatures > 0) {
                formatDescription = ({ intl }) =>
                  intl.formatMessage(messages.continueCollecting, {
                    remaining: remainingSignatures,
                  });
              } else {
                formatDescription = ({ intl }) =>
                  intl.formatMessage(messages.unknownCollecting);
              }
            }
          }
          showNextTransactionInProgress = false;
        }

        return {
          status,
          progressFraction,
          formatDescription,
          isSigningRound: true,
          signers: [...transaction.signers].sort((a, b) => {
            const decidedA =
              (a.dateRejected ? 1 : 0) +
              (!a.dateRejected && !a.dateSigned ? 2 : 0);
            const decidedB =
              (b.dateRejected ? 1 : 0) +
              (!b.dateRejected && !b.dateSigned ? 2 : 0);
            if (decidedA !== decidedB) {
              return decidedA - decidedB;
            }
            if (decidedA < 2) {
              const timeA = a.dateRejected || a.dateSigned;
              const timeB = b.dateRejected || b.dateSigned;
              return timeA - timeB;
            } else {
              return a.handle.localeCompare(b.handle);
            }
          }),
          transactionIndex,
        };
      });

    timeline = [...proposalEvent, ...signingRounds, ...timeline];
    if (previousEvents) {
      timeline = [
        ...previousEvents.map((event) => ({
          status: 'completed',
          ...event,
        })),
        ...timeline,
      ];
    }
    return timeline;
  }

  /** @internal */
  transformLockboxCreate(
    event,
    {
      profileId,
      entityId,
      isPersonalProfile,
      ownHandle,
      lockboxes: allLockboxes,
    },
  ) {
    const lockboxId = event?.lockboxId;
    const lockbox = event?.lockbox;
    const signers = lockbox?.signers || [];
    const stage = event?.stage;

    const isEventForThisEntity = !!entityId && lockbox?.entityId === entityId;
    const isAccountCreation = isEventForThisEntity && isPersonalProfile;

    const numberOfAdministrators = lockbox?.signers?.length || 1;
    const requiredSigners = lockbox?.requiredSigners || 1;
    const proposer = signers.find((signer) => signer?.isProposer);
    const proposerHandle = proposer?.handle;
    const isProposer =
      (lockbox?.signers || []).some(
        (signer) =>
          signer?.isProposer && ownHandle && signer?.handle === ownHandle,
      ) || numberOfAdministrators === 1;
    const invitationsSent = stage !== 'creating' && stage !== 'confirming';
    const numberOfAcceptedAdministrators = signers.filter(
      (signer) => signer?.dateAccepted,
    ).length;
    const readyToActivate =
      invitationsSent &&
      numberOfAdministrators - numberOfAcceptedAdministrators === 1;
    let signerToActivate;
    if (readyToActivate) {
      signerToActivate = signers.find(
        (signer) => !signer?.dateAccepted && !signer?.dateRejected,
      );
    }
    const signerToActivateIsMe =
      !!ownHandle && signerToActivate?.handle === ownHandle;
    const haveResponded =
      stage === 'accepting' || stage === 'finalizing' || stage === 'executed'
        ? true
        : signers.some(
            (signer) =>
              ownHandle &&
              signer?.handle === ownHandle &&
              (signer?.dateAccepted || signer?.dateRejected),
          );
    const multisigStateForTimeline = {
      isMultisig: !isAccountCreation,
      invitationsSent,
      signaturesRemaining:
        numberOfAdministrators - numberOfAcceptedAdministrators,
      signatureCount: numberOfAcceptedAdministrators,
    };

    let requiresAction = false;
    let pendingOthers = false;
    let formatDescription;
    let counterpartyVerb = 'from';

    if (isEventForThisEntity) {
      counterpartyVerb = null;
      if (event?.terminationReason) {
        formatDescription = isPersonalProfile
          ? (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":true,"isNewAdministration":false,"terminationReason":true,"isPersonalProfile":true}',
                defaultMessage: 'Account creation failed',
              })
          : (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":true,"isNewAdministration":false,"terminationReason":true,"isPersonalProfile":false}',
                defaultMessage: 'Workspace creation failed',
              });
      } else {
        switch (stage) {
          case 'creating':
          case 'confirming':
          case 'proposal':
            pendingOthers = true;
            formatDescription = isPersonalProfile
              ? (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":true,"isNewAdministration":false,"terminationReason":false,"stage":"proposal","isPersonalProfile":true}',
                    defaultMessage: 'Awaiting account creation',
                  })
              : (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":true,"isNewAdministration":false,"terminationReason":false,"stage":"proposal","isPersonalProfile":false}',
                    defaultMessage: 'Awaiting workspace creation',
                  });
            break;
          case 'executed':
            formatDescription = isPersonalProfile
              ? (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":true,"isNewAdministration":false,"terminationReason":false,"stage":"executed","isPersonalProfile":true}',
                    defaultMessage: 'Account created',
                  })
              : (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":true,"isNewAdministration":false,"terminationReason":false,"stage":"executed","isPersonalProfile":false}',
                    defaultMessage: 'Workspace created',
                  });
            break;
          case 'connecting':
          case 'accepting':
          case 'finalizing':
          default:
            pendingOthers = true;
            formatDescription = isPersonalProfile
              ? (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":true,"isNewAdministration":false,"terminationReason":false,"isPersonalProfile":true}',
                    defaultMessage: 'Creating account…',
                  })
              : (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":true,"isNewAdministration":false,"terminationReason":false,"isPersonalProfile":false}',
                    defaultMessage: 'Creating workspace…',
                  });
            break;
        }
      }
    } else {
      if (event?.terminationReason) {
        switch (event.terminationReason) {
          case 'cancelled':
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":false,"isNewAdministration":false,"terminationReason":"cancelled"}',
                defaultMessage: 'Cancelled workspace invitation',
              });
            break;
          case 'revoked':
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":false,"isNewAdministration":false,"terminationReason":"revoked"}',
                defaultMessage: 'Workspace invitation revoked',
              });
            break;
          case 'rejected':
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":false,"isNewAdministration":false,"terminationReason":"rejected"}',
                defaultMessage: 'Workspace invitation blocked',
              });
            break;
          case 'expired':
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":false,"isNewAdministration":false,"terminationReason":"expired"}',
                defaultMessage: 'Workspace invitation expired',
              });
            break;
          case 'failed':
          default:
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":false,"isNewAdministration":false,"terminationReason":true}',
                defaultMessage: 'Workspace invitation failed',
              });
            break;
        }
      } else {
        switch (stage) {
          case 'creating':
          case 'confirming':
            requiresAction = true;
            counterpartyVerb = null;
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":false,"isNewAdministration":false,"terminationReason":false,"stage":["creating","confirming"]}',
                defaultMessage: 'New workspace proposal',
              });
            break;
          case 'proposal':
            if (readyToActivate) {
              if (signerToActivateIsMe) {
                requiresAction = true;
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":false,"isNewAdministration":false,"terminationReason":false,"stage":"proposal","readyToActivate":true,"signerToActivateIsMe":true}',
                    defaultMessage: 'Activate workspace',
                  });
                counterpartyVerb = null;
              } else {
                pendingOthers = true;
                if (isProposer) {
                  formatDescription = (intl) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":false,"isNewAdministration":false,"terminationReason":false,"stage":"proposal","readyToActivate":true,"signerToActivateIsMe":true,"isProposer":true}',
                      defaultMessage: 'Proposed workspace, awaiting activation',
                    });
                  counterpartyVerb = null;
                } else {
                  formatDescription = (intl) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":false,"isNewAdministration":false,"terminationReason":false,"stage":"proposal","readyToActivate":true,"signerToActivateIsMe":true,"isProposer":false}',
                      defaultMessage:
                        'Accepted invitation, awaiting activation',
                    });
                }
              }
            } else {
              if (haveResponded) {
                pendingOthers = true;
                if (isProposer) {
                  formatDescription = (intl) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":false,"isNewAdministration":false,"terminationReason":false,"stage":"proposal","readyToActivate":false,"haveResponded":true,"isProposer":true}',
                      defaultMessage: 'Proposed workspace, awaiting activation',
                    });
                  counterpartyVerb = null;
                } else {
                  formatDescription = (intl) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":false,"isNewAdministration":false,"terminationReason":false,"stage":"proposal","readyToActivate":false,"haveResponded":true,"isProposer":false}',
                      defaultMessage:
                        'Accepted invitation, awaiting activation',
                    });
                }
              } else {
                if (isProposer) {
                  requiresAction = true;
                  formatDescription = (intl) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":false,"isNewAdministration":false,"terminationReason":false,"stage":"proposal","readyToActivate":false,"haveResponded":false,"isProposer":true}',
                      defaultMessage: 'Review workspace proposal',
                    });
                } else {
                  requiresAction = true;
                  formatDescription = (intl) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":false,"isNewAdministration":false,"terminationReason":false,"stage":"proposal","readyToActivate":false,"haveResponded":false,"isProposer":false}',
                      defaultMessage: 'Review workspace invitation',
                    });
                }
              }
            }
            break;
          case 'executed':
            if (isProposer) {
              counterpartyVerb = null;
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":false,"isNewAdministration":false,"terminationReason":false,"stage":"executed","isProposer":true}',
                  defaultMessage: 'Created workspace',
                });
            } else {
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":false,"isNewAdministration":false,"terminationReason":false,"stage":"executed","isProposer":false}',
                  defaultMessage: 'Accepted workspace invitation',
                });
            }
            break;
          case 'connecting':
          case 'accepting':
          case 'finalizing':
          default:
            pendingOthers = true;
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"lockboxCreate","isEventForThisEntity":false,"isNewAdministration":false,"terminationReason":false}',
                defaultMessage: 'Creating workspace…',
              });
            break;
        }
      }
    }

    let timeline = [];
    if (!isAccountCreation) {
      let pendingActivationEvent;
      if (signerToActivateIsMe) {
        pendingActivationEvent = [
          {
            status: 'progress',
            formatDescription: ({ intl }) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"lockboxCreate","signerToActivateIsMe":true}',
                defaultMessage: 'Ready to activate',
              }),
          },
        ];
      } else if (signerToActivate?.handle) {
        pendingActivationEvent = [
          {
            status: 'progress',
            handle: signerToActivate.handle,
            formatDescription: ({ intl, name }) =>
              intl.formatMessage(
                {
                  id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"lockboxCreate","signerToActivate":true}',
                  defaultMessage: 'Pending activation by {name}',
                },
                {
                  name,
                },
              ),
          },
        ];
      }

      const timelineProposalEvent =
        proposer?.handle && invitationsSent && numberOfAdministrators > 1
          ? [
              {
                status: 'next',
                handle: proposer.handle,
                formatDescription: ({ intl, name }) =>
                  intl.formatMessage(this.multisigTimelineMessages.proposed, {
                    name,
                  }),
                timestamp: event?.dateCreated,
              },
            ]
          : undefined;

      if (event?.terminationReason) {
        switch (event.terminationReason) {
          case 'cancelled':
            timeline = this.makeTimelineForTransactions({
              conclusionEvent: 'expired',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"lockboxCreate","terminationReason":"cancelled"}',
                defaultMessage: 'Cancelled',
              }),
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'revoked':
            timeline = this.makeTimelineForTransactions({
              previousEvents: timelineProposalEvent,
              multisigState: multisigStateForTimeline,
              workspaceInvitationProgress: signers,
              conclusionEvent: 'revoked',
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'rejected':
            timeline = this.makeTimelineForTransactions({
              previousEvents: timelineProposalEvent,
              multisigState: multisigStateForTimeline,
              workspaceInvitationProgress: signers,
              conclusionEvent: 'blocked',
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'expired':
            timeline = this.makeTimelineForTransactions({
              previousEvents: timelineProposalEvent,
              multisigState: multisigStateForTimeline,
              workspaceInvitationProgress: signers,
              conclusionEvent: 'expired',
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'failed':
          default:
            timeline = this.makeTimelineForTransactions({
              previousEvents: timelineProposalEvent,
              multisigState: multisigStateForTimeline,
              workspaceInvitationProgress: signers,
              conclusionEvent: 'failed',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"lockboxCreate","terminationReason":"failed"}',
                defaultMessage: 'Proposal failed',
              }),
              conclusionTimestamp: event?.dateModified,
            });
            break;
        }
      } else {
        timeline = this.makeTimelineForTransactions({
          previousEvents: timelineProposalEvent,
          multisigState: multisigStateForTimeline,
          workspaceInvitationProgress: signers,
          nextEvents: pendingActivationEvent,
        });
      }
    }

    const accept = async (setMfaRequest) => {
      let { handshakeId } =
        await this.tokenizedApi.transfers.acceptLockboxProposal(
          profileId,
          lockboxId,
        );
      if (handshakeId) {
        await this.tokenizedApi.mfa.waitForMfa(handshakeId, setMfaRequest);
      }

      this.tokenizedApi.reloadAll();
    };
    const reject = async () => {
      await this.tokenizedApi.transfers.rejectLockboxProposal(
        profileId,
        lockboxId,
      );
    };
    let actions = {};
    if (requiresAction) {
      actions = { accept, reject };
    } else if (isProposer && pendingOthers && !haveResponded) {
      // Proposers can retract the proposal at any time
      actions = { reject };
    }

    const entity = lockbox?.entityHandle
      ? {
          displayName: lockbox?.entityName,
          displayHandle: lockbox?.entityHandle,
          isInternal: true,
          isVerified: true,
          isAnonymous: false,
        }
      : {};

    return {
      ...event,
      stage,
      requiresAction,
      pendingOthers,
      isEventForThisEntity,
      isAccountCreation,
      numberOfAdministrators,
      requiredSigners,
      isProposer,
      proposerHandle,
      numberOfAcceptedAdministrators,
      readyToActivate,
      haveResponded,
      ...actions,
      entity: {
        ...entity,
        format: this.makeFormatEntityDescription(entity, null),
      },
      lockbox: lockbox && {
        ...lockbox,
        formatted: {
          formatControlArrangement: this.makeFormatControlArrangement(
            lockbox,
            isAccountCreation,
          ),
        },
      },
      formatted: {
        formatDescription,
        formatCounterparty: this.makeFormatEntityDescription(
          entity,
          counterpartyVerb,
        ),
      },
      role: {
        type: 'administrator',
        format: (intl) =>
          intl.formatMessage({
            defaultMessage: 'Administrator',
            description: 'Workspace role description',
            id: 'TokenizedSDK:activity.workspace.role.administrator',
          }),
      },
      timeline,
    };
  }

  /** @internal */
  transformInstrumentSend(event, { profileId, lockboxes, ownHandle }) {
    const isSend = !!event?.isInitiator;

    let requiresAction = false;
    let pendingOthers = false;
    let formatDescription;
    let resumeConfirm, discard;
    let timeline;

    const multisig = this.calculateMultisigState(event, {
      profileId,
      lockboxes,
      ownHandle,
    });
    if (multisig.isMultisig) {
      if (multisig.pendingOthers) {
        pendingOthers = true;
      }
      if (multisig.requiresAction) {
        requiresAction = true;
      }
    }

    if (isSend) {
      if (event?.terminationReason) {
        switch (event?.terminationReason) {
          case 'cancelled':
            if (multisig?.isMultisig) {
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":"cancelled","multisigState":true}',
                  defaultMessage: 'Cancelled transfer proposal',
                });
            } else {
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":"cancelled","multisigState":false}',
                  defaultMessage: 'Cancelled transfer',
                });
            }
            timeline = this.makeTimelineForTransactions({
              conclusionEvent: 'expired',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"payment","isSend":true,"terminationReason":"cancelled"}',
                defaultMessage: 'Confirmation cancelled',
              }),
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'revoked':
            if (multisig?.isMultisig) {
              formatDescription = (intl, { isLiability } = {}) =>
                isLiability
                  ? intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":"revoked","multisigState":true,"isLiability":true}',
                      defaultMessage: 'Issuance proposal retracted',
                    })
                  : intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":"revoked","multisigState":true,"isLiability":false}',
                      defaultMessage: 'Send proposal retracted',
                    });
              timeline = this.makeTimelineForTransactions({
                multisigState: multisig,
                transactions: event?.transactions,
                conclusionEvent: 'revoked',
                conclusionTimestamp: event?.dateModified,
              });
            } else {
              formatDescription = (intl, { isLiability } = {}) =>
                isLiability
                  ? intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":"revoked","multisigState":false,"isLiability":true}',
                      defaultMessage: 'Issuance revoked',
                    })
                  : intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":"revoked","multisigState":false,"isLiability":false}',
                      defaultMessage: 'Send revoked',
                    });
              timeline = this.makeTimelineForTransactions({
                transactions: event?.transactions,
                conclusionEvent: 'revoked',
                conclusionMessage: defineMessage({
                  id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"payment","isSend":true,"terminationReason":"revoked","multisigState":false}',
                  defaultMessage: 'Transfer revoked',
                }),
                conclusionTimestamp: event?.dateModified,
              });
            }
            break;
          case 'rejected':
            if (multisig?.isMultisig && event?.stage === 'proposal') {
              formatDescription = (intl, { isLiability } = {}) =>
                isLiability
                  ? intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":"rejected","multisigState":true,"isLiability":true}',
                      defaultMessage: 'Issuance proposal blocked',
                    })
                  : intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":"rejected","multisigState":true,"isLiability":false}',
                      defaultMessage: 'Send proposal blocked',
                    });
              timeline = this.makeTimelineForTransactions({
                multisigState: multisig,
                transactions: event?.transactions,
                conclusionEvent: 'blocked',
                conclusionTimestamp: event?.dateModified,
              });
            } else {
              formatDescription = (intl, { isLiability } = {}) =>
                isLiability
                  ? intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":"rejected","multisigState":false,"isLiability":true}',
                      defaultMessage: 'Issuance rejected',
                    })
                  : intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":"rejected","multisigState":false,"isLiability":false}',
                      defaultMessage: 'Send rejected',
                    });
              timeline = this.makeTimelineForTransactions({
                multisigState: multisig,
                transactions: event?.transactions,
                conclusionEvent: 'failed',
                conclusionMessage: defineMessage({
                  id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"payment","isSend":true,"terminationReason":"rejected","multisigState":false}',
                  defaultMessage: 'Transfer rejected',
                }),
                conclusionTimestamp: event?.dateModified,
              });
            }
            break;
          case 'expired':
            if (multisig?.isMultisig) {
              formatDescription = (intl, { isLiability } = {}) =>
                isLiability
                  ? intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":"expired","multisigState":true,"isLiability":true}',
                      defaultMessage: 'Issuance proposal expired',
                    })
                  : intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":"expired","multisigState":true,"isLiability":false}',
                      defaultMessage: 'Send proposal expired',
                    });
              timeline = this.makeTimelineForTransactions({
                multisigState: multisig,
                transactions: event?.transactions,
                conclusionEvent: 'expired',
                conclusionTimestamp: event?.dateModified,
              });
            } else {
              formatDescription = (intl, { isLiability } = {}) =>
                isLiability
                  ? intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":"expired","multisigState":false,"isLiability":true}',
                      defaultMessage: 'Issuance expired',
                    })
                  : intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":"expired","multisigState":false,"isLiability":false}',
                      defaultMessage: 'Send expired',
                    });
              timeline = this.makeTimelineForTransactions({
                multisigState: multisig,
                transactions: event?.transactions,
                conclusionEvent: 'expired',
                conclusionMessage: defineMessage({
                  id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"payment","isSend":true,"terminationReason":"expired","multisigState":false}',
                  defaultMessage: 'Transfer expired',
                }),
                conclusionTimestamp: event?.dateModified,
              });
            }
            break;
          case 'failed':
          default:
            formatDescription = (intl, { isLiability } = {}) =>
              isLiability
                ? intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":"failed","isLiability":true}',
                    defaultMessage: 'Issuance failed',
                  })
                : intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":"failed","isLiability":false}',
                    defaultMessage: 'Send failed',
                  });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'failed',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"payment","isSend":true,"terminationReason":"failed"}',
                defaultMessage: 'Transfer failed',
              }),
              conclusionTimestamp: event?.dateModified,
            });
            break;
        }
      } else {
        switch (event?.stage) {
          case 'creating':
            requiresAction = true;
            if (multisig?.isMultisig) {
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":"creating","multisigState":true}',
                  defaultMessage: 'New transfer proposal',
                });
            } else {
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":"creating","multisigState":false}',
                  defaultMessage: 'New transfer',
                });
            }
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
            });
            break;
          case 'connecting':
            pendingOthers = true;
            discard = this.makeCancelProposalCallback(profileId, event);
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":"connecting"}',
                defaultMessage: 'Preparing new transfer',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
            });
            break;
          case 'confirming':
            requiresAction = true;
            resumeConfirm = this.makeResumeConfirmCallback(profileId, event);
            discard = this.makeCancelProposalCallback(profileId, event);
            if (multisig?.isMultisig) {
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":"confirming","multisigState":true}',
                  defaultMessage: 'Confirm prepared transfer proposal',
                });
            } else {
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":"confirming","multisigState":false}',
                  defaultMessage: 'Confirm prepared transfer',
                });
            }
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
            });
            break;
          case 'proposal':
            if (multisig?.isMultisig) {
              switch (multisig.multisigState) {
                case 'proposed':
                  formatDescription = (intl, { isLiability } = {}) =>
                    isLiability
                      ? intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":"proposal","multisigState":"proposed","isLiability":true}',
                          defaultMessage: 'Issuance proposal under review',
                        })
                      : intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":"proposal","multisigState":"proposed","isLiability":false}',
                          defaultMessage: 'Send proposal under review',
                        });
                  break;
                case 'review':
                default:
                  formatDescription = (intl, { isLiability } = {}) =>
                    isLiability
                      ? intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":"proposal","multisigState":["review", true],"isLiability":true}',
                          defaultMessage: 'Review issuance proposal',
                        })
                      : intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":"proposal","multisigState":["review", true]},"isLiability":false',
                          defaultMessage: 'Review send proposal',
                        });
                  break;
                case 'rejected':
                  formatDescription = (intl, { isLiability } = {}) =>
                    isLiability
                      ? intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":"proposal","multisigState":"rejected","isLiability":true}',
                          defaultMessage:
                            'Rejected issuance proposal under review',
                        })
                      : intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":"proposal","multisigState":"rejected","isLiability":false}',
                          defaultMessage: 'Rejected send proposal under review',
                        });
                  break;
                case 'signed':
                  formatDescription = (intl, { isLiability } = {}) =>
                    isLiability
                      ? intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":"proposal","multisigState":"signed","isLiability":true}',
                          defaultMessage:
                            'Approved issuance proposal under review',
                        })
                      : intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":"proposal","multisigState":"signed","isLiability":false}',
                          defaultMessage: 'Approved send proposal under review',
                        });
                  break;
              }
              timeline = this.makeTimelineForTransactions({
                multisigState: multisig,
                transactions: event?.transactions,
              });
            } else {
              pendingOthers = true;
              formatDescription = (intl, { isLiability } = {}) =>
                isLiability
                  ? intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":"proposal","multisigState":false,"isLiability":true}',
                      defaultMessage: 'Issuing instruments…',
                    })
                  : intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":"proposal","multisigState":false,"isLiability":false}',
                      defaultMessage: 'Sending instruments…',
                    });
              timeline = this.makeTimelineForTransactions({
                transactions: event?.transactions,
                conclusionEvent: 'progress',
                conclusionMessage: defineMessage({
                  id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"payment","isSend":true,"terminationReason":false,"stage":"proposal","multisigState":false}',
                  defaultMessage: 'Transfer started',
                }),
                conclusionTimestamp: event?.dateCreated,
                isProcessing: true,
              });
            }
            break;
          case 'executed':
            formatDescription = (intl, { isLiability } = {}) =>
              isLiability
                ? intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":"executed","isLiability":true}',
                    defaultMessage: 'Issued instruments',
                  })
                : intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":"executed","isLiability":false}',
                    defaultMessage: 'Sent instruments',
                  });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'succeeded',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"payment","isSend":true,"terminationReason":false,"stage":"executed"}',
                defaultMessage: 'Transfer executed',
              }),
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'accepting':
          case 'finalizing':
          default:
            pendingOthers = true;
            formatDescription = (intl, { isLiability } = {}) =>
              isLiability
                ? intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":["accepting","finalizing"],"isLiability":true}',
                    defaultMessage: 'Issuing instruments…',
                  })
                : intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend":true,"terminationReason":false,"stage":["accepting","finalizing"],"isLiability":false}',
                    defaultMessage: 'Sending instruments…',
                  });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'progress',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"payment","isSend":true,"terminationReason":false,"stage":["accepting","finalizing"]}',
                defaultMessage: 'Awaiting completion',
              }),
              conclusionTimestamp: event?.dateCreated,
              isProcessing: true,
            });
            break;
        }
      }
    } else {
      if (event?.terminationReason) {
        switch (event?.terminationReason) {
          case 'cancelled':
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend",false,"terminationReason":"cancelled"}',
                defaultMessage: 'Transfer cancelled',
              });
            timeline = this.makeTimelineForTransactions({
              conclusionEvent: 'expired',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"payment","isSend":false,"terminationReason":"cancelled"}',
                defaultMessage: 'Transfer cancelled',
              }),
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'revoked':
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend",false,"terminationReason":"revoked"}',
                defaultMessage: 'Transfer revoked',
              });
            timeline = this.makeTimelineForTransactions({
              transactions: event?.transactions,
              conclusionEvent: 'revoked',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"payment","isSend":false,"terminationReason":"revoked"}',
                defaultMessage: 'Transfer revoked',
              }),
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'rejected':
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend",false,"terminationReason":"rejected"}',
                defaultMessage: 'Transfer rejected',
              });
            timeline = this.makeTimelineForTransactions({
              transactions: event?.transactions,
              conclusionEvent: 'failed',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"payment","isSend":false,"terminationReason":"rejected"}',
                defaultMessage: 'Transfer rejected',
              }),
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'expired':
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend",false,"terminationReason":"expired"}',
                defaultMessage: 'Transfer expired',
              });
            timeline = this.makeTimelineForTransactions({
              transactions: event?.transactions,
              conclusionEvent: 'expired',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"payment","isSend":false,"terminationReason":"expired"}',
                defaultMessage: 'Transfer expired',
              }),
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'failed':
          default:
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend",false,"terminationReason":"failed"}',
                defaultMessage: 'Transfer failed',
              });
            timeline = this.makeTimelineForTransactions({
              transactions: event?.transactions,
              conclusionEvent: 'failed',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"payment","isSend":false,"terminationReason":"failed"}',
                defaultMessage: 'Transfer failed',
              }),
              conclusionTimestamp: event?.dateModified,
            });
            break;
        }
      } else {
        switch (event?.stage) {
          case 'executed':
            if (event?.activityEventType === 'transfer_fee') {
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend",false,"terminationReason":false,"stage":"executed","isTransferFee":true}',
                  defaultMessage: 'Received transfer fee',
                });
            } else {
              formatDescription = (intl, { isLiability } = {}) =>
                isLiability
                  ? intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend",false,"terminationReason":false,"stage":"executed","isTransferFee":false,"isLiability":true}',
                      defaultMessage: 'Instruments redeemed',
                    })
                  : intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend",false,"terminationReason":false,"stage":"executed","isTransferFee":false,"isLiability":false}',
                      defaultMessage: 'Received instruments',
                    });
            }
            timeline = this.makeTimelineForTransactions({
              transactions: event?.transactions,
              conclusionEvent: 'succeeded',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"payment","isSend":false,"terminationReason":false,"stage":"executed"}',
                defaultMessage: 'Transfer executed',
              }),
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'accepting':
          case 'finalizing':
          default:
            pendingOthers = true;
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"payment","isSend",false,"terminationReason":false,"stage":["accepting","finalizing"]}',
                defaultMessage: 'Receiving instruments…',
              });
            timeline = this.makeTimelineForTransactions({
              transactions: event?.transactions,
              conclusionEvent: 'progress',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"payment","isSend":false,"terminationReason":false,"stage":["accepting","finalizing"]}',
                defaultMessage: 'Awaiting completion',
              }),
              conclusionTimestamp: event?.dateCreated,
              isProcessing: true,
            });
            break;
        }
      }
    }

    let feePayingInstrument;
    if (event?.activityEventType === 'transfer_fee') {
      feePayingInstrument = event?.instruments?.find(
        ({ instrumentId }) => instrumentId !== CURRENCY_BSV,
      );
    }

    const counterparty = event?.counterparties?.[0];
    const formatted = {
      formatDescription,
      formatCounterparty: this.makeFormatEntityDescription(
        counterparty,
        event?.isInitiator ? 'to' : 'from',
      ),
      ...(feePayingInstrument
        ? {
            formatFeePayingInstrument: this.makeFormatTransferDescription(
              feePayingInstrument,
              'forTransfer',
            ),
          }
        : {}),
    };
    const transformedEvent = {
      ...event,
      ...multisig,
      timeline,
      formatted,
      requiresAction,
      pendingOthers,
      resumeConfirm,
      discard,
    };
    if (feePayingInstrument) {
      transformedEvent.feePayingInstrumentId = feePayingInstrument.instrumentId;
    }
    if (event?.counterparties) {
      transformedEvent.counterparties = event.counterparties.map((party) => ({
        ...party,
        format: this.makeFormatEntityDescription(party, isSend ? 'to' : 'from'),
        transfers: party.transfers?.map?.((transfer) => ({
          ...transfer,
          format: this.makeFormatTransferDescription(transfer),
        })),
      }));
    }
    return transformedEvent;
  }

  /** @internal */
  transformInstrumentRequest(event, { profileId, lockboxes, ownHandle }) {
    const counterparty = event?.counterparties?.[0];
    const isInitiator = event?.isInitiator;
    const transferReceive = event?.counterparties?.[0]?.transfers?.[0];
    const lockbox = lockboxes?.idMap?.get(
      event?.lockboxId || event?.counterparties?.[0]?.povLockboxId,
    );

    let requiresAction = false;
    let pendingOthers = false;
    let formatDescription;
    let counterpartyVerb = isInitiator ? 'to' : 'from';
    let acceptRequest;
    let declineRequest;
    let revokeRequest;
    let timeline;

    const multisig = this.calculateMultisigState(event, {
      profileId,
      lockboxes,
      ownHandle,
    });
    if (multisig.isMultisig) {
      if (multisig.pendingOthers) {
        pendingOthers = true;
      }
      if (multisig.requiresAction) {
        requiresAction = true;
      }
    }

    if (event?.terminationReason) {
      switch (event.terminationReason) {
        case 'cancelled':
          formatDescription = (intl) =>
            intl.formatMessage({
              id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":"cancelled"}',
              defaultMessage: 'Cancelled instrument request',
            });
          timeline = this.makeTimelineForTransactions({
            conclusionEvent: 'expired',
            conclusionMessage: defineMessage({
              id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":"cancelled"}',
              defaultMessage: 'Confirmation cancelled',
            }),
            conclusionTimestamp: event?.dateModified,
          });
          break;
        case 'revoked':
          switch (event?.stage) {
            case 'proposal':
              counterpartyVerb = 'by';
              if (multisig?.isMultisig) {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":"revoked","stage":"proposal","multisigState":true}',
                    defaultMessage: 'Instrument request proposal retracted',
                  });
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  transactions: event?.transactions,
                  conclusionEvent: 'revoked',
                  conclusionTimestamp: event?.dateModified,
                });
              } else {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":"revoked","stage":"proposal","multisigState":false}',
                    defaultMessage: 'Instrument request revoked',
                  });
                timeline = this.makeTimelineForTransactions({
                  transactions: event?.transactions,
                  conclusionEvent: 'revoked',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":"revoked","stage":"proposal","multisigState":false}',
                    defaultMessage: 'Request revoked',
                  }),
                  conclusionTimestamp: event?.dateModified,
                });
              }
              break;
            case 'reviewing':
              if (isInitiator) {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":"revoked","stage":"reviewing","isInitiator":true}',
                    defaultMessage: 'Retracted instrument request',
                  });
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  transactions: event?.transactions,
                  // In this case the transaction is for the offer send
                  conclusionEvent: 'next',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":"revoked","stage":"reviewing","isInitiator":true}',
                    defaultMessage: 'Request sent',
                  }),
                  nextEvents: [
                    {
                      status: 'failed',
                      formatDescription: ({ intl }) =>
                        intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"request","terminationReason":"revoked","stage":"reviewing","isInitiator":true}',
                          defaultMessage: 'Request retracted',
                        }),
                      timestamp: event?.dateModified,
                    },
                  ],
                });
              } else {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":"revoked","stage":"reviewing","isInitiator":false}',
                    defaultMessage: 'Instrument request retracted',
                  });
                counterpartyVerb = 'by';
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  previousEvents: [
                    {
                      status: 'next',
                      formatDescription: ({ intl }) =>
                        intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":"revoked","stage":"reviewing","isInitiator":false}',
                          defaultMessage: 'Request received',
                        }),
                      timestamp: event?.dateCreated,
                    },
                  ],
                  transactions: event?.transactions,
                  multisigFallbackTransaction: {
                    dateCancelled: event?.dateModified,
                    signers: lockbox?.signers,
                  },
                  conclusionEvent: 'failed',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"request","terminationReason":"revoked","stage":"reviewing","isInitiator":false}',
                    defaultMessage: 'Request retracted',
                  }),
                  conclusionTimestamp: event?.dateModified,
                });
              }
              break;
            default:
              counterpartyVerb = 'by';
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":"revoked"}',
                  defaultMessage: 'Instrument request revoked',
                });
              timeline = this.makeTimelineForTransactions({
                transactions: event?.transactions,
                conclusionEvent: 'revoked',
                conclusionMessage: defineMessage({
                  id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":"revoked"}',
                  defaultMessage: 'Request revoked',
                }),
                conclusionTimestamp: event?.dateModified,
              });
              break;
          }
          break;
        case 'rejected':
          switch (event?.stage) {
            case 'proposal':
              if (multisig?.isMultisig) {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":"rejected","stage":"proposal","multisigState":true}',
                    defaultMessage: 'Instrument request proposal blocked',
                  });
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  transactions: event?.transactions,
                  conclusionEvent: 'blocked',
                  conclusionTimestamp: event?.dateModified,
                });
              } else {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":"rejected","stage":"proposal","multisigState":false}',
                    defaultMessage: 'Instrument request rejected',
                  });
                timeline = this.makeTimelineForTransactions({
                  transactions: event?.transactions,
                  conclusionEvent: 'failed',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":"rejected","stage":"proposal","multisigState":false}',
                    defaultMessage: 'Request rejected',
                  }),
                  conclusionTimestamp: event?.dateModified,
                });
              }
              break;
            case 'reviewing':
              if (isInitiator) {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":"rejected","stage":"reviewing","isInitiator":true}',
                    defaultMessage: 'Instrument request declined',
                  });
                counterpartyVerb = 'by';
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  transactions: event?.transactions,
                  // In this case the transaction is for the offer send
                  conclusionEvent: 'next',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":"rejected","stage":"reviewing","isInitiator":true}',
                    defaultMessage: 'Request sent',
                  }),
                  nextEvents: [
                    {
                      status: 'failed',
                      formatDescription: ({ intl }) =>
                        intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"request","terminationReason":"rejected","stage":"reviewing","isInitiator":true}',
                          defaultMessage: 'Request declined',
                        }),
                      timestamp: event?.dateModified,
                    },
                  ],
                });
              } else {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":"rejected","stage":"reviewing","isInitiator":false}',
                    defaultMessage: 'Declined instrument request',
                  });
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  previousEvents: [
                    {
                      status: 'next',
                      formatDescription: ({ intl }) =>
                        intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":"rejected","stage":"reviewing","isInitiator":false}',
                          defaultMessage: 'Request received',
                        }),
                      timestamp: event?.dateCreated,
                    },
                  ],
                  transactions: event?.transactions,
                  multisigFallbackTransaction: {
                    dateCancelled: event?.dateModified,
                    signers: lockbox?.signers,
                  },
                  conclusionEvent: 'failed',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"request","terminationReason":"rejected","stage":"reviewing","isInitiator":false}',
                    defaultMessage: 'Request declined',
                  }),
                  conclusionTimestamp: event?.dateModified,
                });
              }
              break;
            default:
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":"rejected"}',
                  defaultMessage: 'Instrument request rejected',
                });
              timeline = this.makeTimelineForTransactions({
                transactions: event?.transactions,
                conclusionEvent: 'failed',
                conclusionMessage: defineMessage({
                  id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":"rejected"}',
                  defaultMessage: 'Request rejected',
                }),
                conclusionTimestamp: event?.dateModified,
              });
              break;
          }
          break;
        case 'expired':
          switch (event?.stage) {
            case 'proposal':
              if (multisig?.isMultisig) {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":"expired","stage":"proposal","multisigState":true}',
                    defaultMessage: 'Instrument request proposal expired',
                  });
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  transactions: event?.transactions,
                  conclusionEvent: 'expired',
                  conclusionTimestamp: event?.dateModified,
                });
              } else {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":"expired","stage":"proposal","multisigState":false}',
                    defaultMessage: 'Instrument request expired',
                  });
                timeline = this.makeTimelineForTransactions({
                  transactions: event?.transactions,
                  conclusionEvent: 'expired',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":"expired","stage":"proposal","multisigState":false}',
                    defaultMessage: 'Request expired',
                  }),
                  conclusionTimestamp: event?.dateModified,
                });
              }
              break;
            case 'reviewing':
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":"expired","stage":"reviewing"}',
                  defaultMessage: 'Instrument request expired',
                });
              if (isInitiator) {
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  transactions: event?.transactions,
                  // In this case the transaction is for the offer send
                  conclusionEvent: 'next',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":"expired","stage":"reviewing","isInitiator":true}',
                    defaultMessage: 'Request sent',
                  }),
                  nextEvents: [
                    {
                      status: 'failed',
                      formatDescription: ({ intl }) =>
                        intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"request","terminationReason":"expired","stage":"reviewing","isInitiator":true}',
                          defaultMessage: 'Request expired',
                        }),
                      timestamp: event?.dateModified,
                    },
                  ],
                });
              } else {
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  previousEvents: [
                    {
                      status: 'next',
                      formatDescription: ({ intl }) =>
                        intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":"expired","stage":"reviewing","isInitiator":false}',
                          defaultMessage: 'Request received',
                        }),
                      timestamp: event?.dateCreated,
                    },
                  ],
                  transactions: event?.transactions,
                  multisigFallbackTransaction: {
                    dateCancelled: event?.dateModified,
                    signers: lockbox?.signers,
                  },
                  conclusionEvent: 'failed',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"request","terminationReason":"expired","stage":"reviewing","isInitiator":false}',
                    defaultMessage: 'Request expired',
                  }),
                  conclusionTimestamp: event?.dateModified,
                });
              }
              break;
            default:
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":"expired"}',
                  defaultMessage: 'Instrument request expired',
                });
              timeline = this.makeTimelineForTransactions({
                transactions: event?.transactions,
                conclusionEvent: 'expired',
                conclusionMessage: defineMessage({
                  id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":"expired"}',
                  defaultMessage: 'Request expired',
                }),
                conclusionTimestamp: event?.dateModified,
              });
              break;
          }
          break;
        case 'failed':
        default:
          formatDescription = (intl) =>
            intl.formatMessage({
              id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":"failed"}',
              defaultMessage: 'Instrument request failed',
            });
          timeline = this.makeTimelineForTransactions({
            transactions: event?.transactions,
            conclusionEvent: 'failed',
            conclusionMessage: defineMessage({
              id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":"failed"}',
              defaultMessage: 'Request failed',
            }),
            conclusionTimestamp: event?.dateModified,
          });
          break;
      }
    } else {
      switch (event?.stage) {
        // Note that because an instrument request doesn’t move any of the
        // initiator’s assets, sending the request doesn’t require recipient
        // details to be fetched ('connecting'), and MFA ('connecting') is also
        // skipped.
        case 'creating':
          requiresAction = true;
          formatDescription = (intl) =>
            intl.formatMessage({
              id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":false,"stage":"creating"}',
              defaultMessage: 'New instrument request',
            });
          timeline = [];
          break;
        case 'connecting':
          pendingOthers = true;
          formatDescription = (intl) =>
            intl.formatMessage({
              id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":false,"stage":"connecting"}',
              defaultMessage: 'Preparing new instrument request',
            });
          timeline = [];
          break;
        case 'confirming':
          // Shouldn’t happen for a request (no confirmation required)
          requiresAction = true;
          formatDescription = (intl) =>
            intl.formatMessage({
              id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":false,"stage":"confirming"}',
              defaultMessage: 'Confirm prepared instrument request',
            });
          timeline = [];
          break;
        case 'proposal':
          // Shouldn’t happen for a request (no proposal required)
          pendingOthers = true;
          formatDescription = (intl) =>
            intl.formatMessage({
              id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":false,"stage":"proposal"}',
              defaultMessage: 'Sending instrument request…',
            });
          counterpartyVerb = 'to';
          timeline = this.makeTimelineForTransactions({
            transactions: event?.transactions,
            conclusionEvent: 'progress',
            conclusionMessage: defineMessage({
              id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":false,"stage":"proposal"}',
              defaultMessage: 'Request started',
            }),
            conclusionTimestamp: event?.dateCreated,
            isProcessing: true,
          });
          break;
        case 'reviewing':
          if (isInitiator) {
            pendingOthers = true;
            if (!multisig?.isMultisig) {
              revokeRequest = this.makeRejectCallback(profileId, event);
            }
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":false,"stage":"reviewing","isInitiator":true}',
                defaultMessage: 'Requested instruments, awaiting response',
              });
            counterpartyVerb = 'from';
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'next',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":false,"stage":"reviewing","isInitiator":true}',
                defaultMessage: 'Request sent',
              }),
              nextEvents: [
                {
                  status: 'progress',
                  formatDescription: ({ intl }) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"request","terminationReason":false,"stage":"reviewing","isInitiator":true}',
                      defaultMessage: 'Pending request fulfillment',
                    }),
                },
              ],
            });
          } else {
            counterpartyVerb = 'from';
            if (multisig?.isMultisig) {
              switch (multisig.multisigState) {
                case 'review':
                default:
                  formatDescription = (intl) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":false,"stage":"reviewing","isInitiator":false,"multisigState":["review", true]}',
                      defaultMessage: 'Review instrument request',
                    });
                  break;
                case 'rejected':
                  formatDescription = (intl) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":false,"stage":"reviewing","isInitiator":false,"multisigState":"rejected"}',
                      defaultMessage:
                        'Rejected instrument request under review',
                    });
                  break;
                case 'signed':
                  formatDescription = (intl) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":false,"stage":"reviewing","isInitiator":false,"multisigState":"signed"}',
                      defaultMessage:
                        'Approved instrument request under review',
                    });
                  break;
              }
            } else {
              requiresAction = true;
              acceptRequest = this.makeApproveCallback(profileId, event);
              declineRequest = this.makeRejectCallback(profileId, event);
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":false,"stage":"reviewing","isInitiator":false,"multisigState":false}',
                  defaultMessage: 'Review instrument request',
                });
            }
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              previousEvents: [
                {
                  status: 'next',
                  formatDescription: ({ intl }) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":false,"stage":"reviewing","isInitiator":false}',
                      defaultMessage: 'Request received',
                    }),
                  timestamp: event?.dateCreated,
                },
              ],
              transactions: event?.transactions,
              multisigFallbackTransaction: {
                signers: lockbox?.signers,
              },
              nextEvents: [
                {
                  status: multisig?.isMultisig ? 'pending' : 'progress',
                  formatDescription: ({ intl }) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"request","terminationReason":false,"stage":"reviewing","isInitiator":false}',
                      defaultMessage: 'Ready to fulfill',
                    }),
                },
              ],
            });
          }
          break;
        case 'executed':
          if (isInitiator) {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":false,"stage":"executed","isInitiator":true}',
                defaultMessage: 'Received instruments by request',
              });
            counterpartyVerb = 'from';
          } else {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":false,"stage":"executed","isInitiator":false}',
                defaultMessage: 'Sent instruments by request',
              });
            counterpartyVerb = 'to';
          }
          timeline = this.makeTimelineForTransactions({
            multisigState: multisig,
            previousEvents: isInitiator
              ? undefined
              : [
                  {
                    status: 'next',
                    formatDescription: ({ intl }) =>
                      intl.formatMessage({
                        id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":false,"stage":"executed","isInitiator":false}',
                        defaultMessage: 'Request received',
                      }),
                    timestamp: event?.dateCreated,
                  },
                ],
            transactions: event?.transactions,
            insertBeforeConclusion: isInitiator
              ? [
                  {
                    status: 'next',
                    formatDescription: ({ intl }) =>
                      intl.formatMessage({
                        id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":false,"stage":"executed","isInitiator":true}',
                        defaultMessage: 'Request sent',
                      }),
                  },
                ]
              : undefined,
            conclusionEvent: 'succeeded',
            conclusionMessage: defineMessage({
              id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"request","terminationReason":false,"stage":"executed"}',
              defaultMessage: 'Request fulfilled',
            }),
            conclusionTimestamp: event?.dateModified,
          });
          break;
        case 'accepting':
        case 'finalizing':
        default:
          pendingOthers = true;
          if (isInitiator) {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":false,"stage":["accepting","finalizing"],"isInitiator":true}',
                defaultMessage: 'Receiving instruments by request…',
              });
            timeline = this.makeTimelineForTransactions({
              transactions: event?.transactions,
              insertBeforeConclusion: [
                {
                  status: 'next',
                  formatDescription: ({ intl }) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":false,"stage":["accepting","finalizing"],"isInitiator":true}',
                      defaultMessage: 'Request sent',
                    }),
                },
              ],
              conclusionEvent: 'progress',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"request","terminationReason":false,"stage":["accepting","finalizing"],"isInitiator":true}',
                defaultMessage: 'Awaiting completion',
              }),
              conclusionTimestamp: event?.dateModified,
              isProcessing: true,
            });
          } else {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"request","terminationReason":false,"stage":["accepting","finalizing"],"isInitiator":false}',
                defaultMessage: 'Sending instruments by request…',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              previousEvents: [
                {
                  status: 'next',
                  formatDescription: ({ intl }) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"request","terminationReason":false,"stage":["accepting","finalizing"],"isInitiator":false}',
                      defaultMessage: 'Request received',
                    }),
                  timestamp: event?.dateCreated,
                },
              ],
              transactions: event?.transactions,
              conclusionEvent: 'progress',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"request","terminationReason":false,"stage":["accepting","finalizing"],"isInitiator":false}',
                defaultMessage: 'Awaiting completion',
              }),
              conclusionTimestamp: event?.dateModified,
              isProcessing: true,
            });
          }
          break;
      }
    }

    const formatted = {
      formatDescription,
      formatCounterparty: this.makeFormatEntityDescription(
        counterparty,
        counterpartyVerb,
      ),
      formatResponseInstrument:
        this.makeFormatTransferDescription(transferReceive),
    };
    const transformedEvent = {
      ...event,
      ...multisig,
      timeline,
      isTransferRequest: true,
      requiresAction,
      pendingOthers,
      formatted,
      acceptRequest,
      declineRequest,
      revokeRequest,
    };
    if (event?.counterparties) {
      transformedEvent.counterparties = event.counterparties.map((party) => ({
        ...party,
        format: this.makeFormatEntityDescription(party, counterpartyVerb, true),
        transfers: party.transfers?.map?.((transfer) => ({
          ...transfer,
          format: this.makeFormatTransferDescription(transfer),
        })),
      }));
    }
    return transformedEvent;
  }

  /** @internal */
  transformInstrumentTrade(event, { profileId, lockboxes, ownHandle }) {
    const counterparty = event?.counterparties?.[0];
    const isInitiator = event?.isInitiator;
    const transferReceive = event?.counterparties?.[0]?.transfers?.find?.(
      (transfer) => transfer.direction === 'received',
    );
    const transferSend = event?.counterparties?.[0]?.transfers?.find?.(
      (transfer) => transfer.direction === 'sent',
    );
    const lockbox = lockboxes?.idMap?.get(
      event?.lockboxId || event?.counterparties?.[0]?.povLockboxId,
    );

    let requiresAction = false;
    let pendingOthers = false;
    let formatDescription;
    let counterpartyVerb = 'with';
    let executeTrade, declineTrade, revokeTrade;
    let resumeConfirm, discard;
    let timeline;

    const multisig = this.calculateMultisigState(event, {
      profileId,
      lockboxes,
      ownHandle,
    });
    if (multisig.isMultisig) {
      if (multisig.pendingOthers) {
        pendingOthers = true;
      }
      if (multisig.requiresAction) {
        requiresAction = true;
      }
    }

    if (event?.terminationReason) {
      switch (event?.terminationReason) {
        case 'cancelled':
          if (multisig?.isMultisig) {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":"cancelled","multisigState":true}',
                defaultMessage: 'Cancelled trade proposal',
              });
          } else {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":"cancelled","multisigState":false}',
                defaultMessage: 'Cancelled trade',
              });
          }
          timeline = this.makeTimelineForTransactions({
            conclusionEvent: 'expired',
            conclusionMessage: defineMessage({
              id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":"cancelled"}',
              defaultMessage: 'Confirmation cancelled',
            }),
            conclusionTimestamp: event?.dateModified,
          });
          break;
        case 'revoked':
          switch (event?.stage) {
            case 'proposal':
              if (multisig?.isMultisig) {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":"revoked","stage":"proposal","multisigState":true}',
                    defaultMessage: 'Trade proposal retracted',
                  });
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  transactions: event?.transactions,
                  conclusionEvent: 'revoked',
                  conclusionTimestamp: event?.dateModified,
                });
              } else {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":"revoked","stage":"proposal","multisigState":false}',
                    defaultMessage: 'Trade revoked',
                  });
                timeline = this.makeTimelineForTransactions({
                  transactions: event?.transactions,
                  conclusionEvent: 'revoked',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":"revoked","stage":"proposal","multisigState":false}',
                    defaultMessage: 'Trade revoked',
                  }),
                  conclusionTimestamp: event?.dateModified,
                });
              }
              break;
            case 'reviewing':
              if (isInitiator) {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":"revoked","stage":"reviewing","isInitiator":true}',
                    defaultMessage: 'Retracted trade offer',
                  });
                counterpartyVerb = 'with';
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  transactions: event?.transactions,
                  // In this case the transaction is for the offer send
                  conclusionEvent: 'next',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":"revoked","stage":"reviewing","isInitiator":true}',
                    defaultMessage: 'Offer sent',
                  }),
                  nextEvents: [
                    {
                      status: 'failed',
                      formatDescription: ({ intl }) =>
                        intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"trade","terminationReason":"revoked","stage":"reviewing","isInitiator":true}',
                          defaultMessage: 'Offer retracted',
                        }),
                      timestamp: event?.dateModified,
                    },
                  ],
                });
              } else {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":"revoked","stage":"reviewing","isInitiator":false}',
                    defaultMessage: 'Trade offer retracted',
                  });
                counterpartyVerb = 'by';
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  previousEvents: [
                    {
                      status: 'next',
                      formatDescription: ({ intl }) =>
                        intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":"revoked","stage":"reviewing","isInitiator":false}',
                          defaultMessage: 'Offer received',
                        }),
                      timestamp: event?.dateCreated,
                    },
                  ],
                  transactions: event?.transactions,
                  multisigFallbackTransaction: {
                    dateCancelled: event?.dateModified,
                    signers: lockbox?.signers,
                  },
                  conclusionEvent: 'failed',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"trade","terminationReason":"revoked","stage":"reviewing","isInitiator":false}',
                    defaultMessage: 'Offer retracted',
                  }),
                  conclusionTimestamp: event?.dateModified,
                });
              }
              break;
            default:
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":"revoked"}',
                  defaultMessage: 'Trade revoked',
                });
              timeline = this.makeTimelineForTransactions({
                transactions: event?.transactions,
                conclusionEvent: 'revoked',
                conclusionMessage: defineMessage({
                  id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":"revoked"}',
                  defaultMessage: 'Trade revoked',
                }),
                conclusionTimestamp: event?.dateModified,
              });
              break;
          }
          break;
        case 'rejected':
          switch (event?.stage) {
            case 'proposal':
              if (multisig?.isMultisig) {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":"rejected","stage":"proposal","multisigState":true}',
                    defaultMessage: 'Trade proposal blocked',
                  });
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  transactions: event?.transactions,
                  conclusionEvent: 'blocked',
                  conclusionTimestamp: event?.dateModified,
                });
              } else {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":"rejected","stage":"proposal","multisigState":false}',
                    defaultMessage: 'Trade rejected',
                  });
                timeline = this.makeTimelineForTransactions({
                  transactions: event?.transactions,
                  conclusionEvent: 'failed',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":"rejected","stage":"proposal","multisigState":false}',
                    defaultMessage: 'Trade rejected',
                  }),
                  conclusionTimestamp: event?.dateModified,
                });
              }
              break;
            case 'reviewing':
              if (isInitiator) {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":"rejected","stage":"reviewing","isInitiator":true}',
                    defaultMessage: 'Trade offer declined',
                  });
                counterpartyVerb = 'by';
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  transactions: event?.transactions,
                  // In this case the transaction is for the offer send
                  conclusionEvent: 'next',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":"rejected","stage":"reviewing","isInitiator":true}',
                    defaultMessage: 'Offer sent',
                  }),
                  nextEvents: [
                    {
                      status: 'failed',
                      formatDescription: ({ intl }) =>
                        intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"trade","terminationReason":"rejected","stage":"reviewing","isInitiator":true}',
                          defaultMessage: 'Offer declined',
                        }),
                      timestamp: event?.dateModified,
                    },
                  ],
                });
              } else {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":"rejected","stage":"reviewing","isInitiator":false}',
                    defaultMessage: 'Declined trade offer',
                  });
                counterpartyVerb = 'with';
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  previousEvents: [
                    {
                      status: 'next',
                      formatDescription: ({ intl }) =>
                        intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":"rejected","stage":"reviewing","isInitiator":false}',
                          defaultMessage: 'Offer received',
                        }),
                      timestamp: event?.dateCreated,
                    },
                  ],
                  transactions: event?.transactions,
                  multisigFallbackTransaction: {
                    dateCancelled: event?.dateModified,
                    signers: lockbox?.signers,
                  },
                  conclusionEvent: 'failed',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"trade","terminationReason":"rejected","stage":"reviewing","isInitiator":false}',
                    defaultMessage: 'Offer declined',
                  }),
                  conclusionTimestamp: event?.dateModified,
                });
              }
              break;
            default:
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":"rejected"}',
                  defaultMessage: 'Trade rejected',
                });
              timeline = this.makeTimelineForTransactions({
                transactions: event?.transactions,
                conclusionEvent: 'failed',
                conclusionMessage: defineMessage({
                  id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":"rejected"}',
                  defaultMessage: 'Trade rejected',
                }),
                conclusionTimestamp: event?.dateModified,
              });
              break;
          }
          break;
        case 'expired':
          switch (event?.stage) {
            case 'proposal':
              if (multisig?.isMultisig) {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":"expired","stage":"proposal","multisigState":true}',
                    defaultMessage: 'Trade proposal expired',
                  });
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  transactions: event?.transactions,
                  conclusionEvent: 'expired',
                  conclusionTimestamp: event?.dateModified,
                });
              } else {
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":"expired","stage":"proposal","multisigState":false}',
                    defaultMessage: 'Trade expired',
                  });
                timeline = this.makeTimelineForTransactions({
                  transactions: event?.transactions,
                  conclusionEvent: 'expired',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":"expired","stage":"proposal","multisigState":false}',
                    defaultMessage: 'Trade expired',
                  }),
                  conclusionTimestamp: event?.dateModified,
                });
              }
              break;
            case 'reviewing':
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":"expired","stage":"reviewing"}',
                  defaultMessage: 'Trade offer expired',
                });
              if (isInitiator) {
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  transactions: event?.transactions,
                  // In this case the transaction is for the offer send
                  conclusionEvent: 'next',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":"expired","stage":"reviewing","isInitiator":true}',
                    defaultMessage: 'Offer sent',
                  }),
                  nextEvents: [
                    {
                      status: 'failed',
                      formatDescription: ({ intl }) =>
                        intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"trade","terminationReason":"expired","stage":"reviewing","isInitiator":true}',
                          defaultMessage: 'Offer expired',
                        }),
                      timestamp: event?.dateModified,
                    },
                  ],
                });
              } else {
                counterpartyVerb = 'from';
                timeline = this.makeTimelineForTransactions({
                  multisigState: multisig,
                  previousEvents: [
                    {
                      status: 'next',
                      formatDescription: ({ intl }) =>
                        intl.formatMessage({
                          id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":"expired","stage":"reviewing","isInitiator":false}',
                          defaultMessage: 'Offer received',
                        }),
                      timestamp: event?.dateCreated,
                    },
                  ],
                  transactions: event?.transactions,
                  multisigFallbackTransaction: {
                    dateCancelled: event?.dateModified,
                    signers: lockbox?.signers,
                  },
                  conclusionEvent: 'failed',
                  conclusionMessage: defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"trade","terminationReason":"expired","stage":"reviewing","isInitiator":false}',
                    defaultMessage: 'Offer expired',
                  }),
                  conclusionTimestamp: event?.dateModified,
                });
              }
              break;
            default:
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":"expired"}',
                  defaultMessage: 'Trade expired',
                });
              timeline = this.makeTimelineForTransactions({
                transactions: event?.transactions,
                conclusionEvent: 'expired',
                conclusionMessage: defineMessage({
                  id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":"expired"}',
                  defaultMessage: 'Trade expired',
                }),
                conclusionTimestamp: event?.dateModified,
              });
              break;
          }
          break;
        case 'failed':
        default:
          formatDescription = (intl) =>
            intl.formatMessage({
              id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":"failed"}',
              defaultMessage: 'Trade failed',
            });
          timeline = this.makeTimelineForTransactions({
            transactions: event?.transactions,
            conclusionEvent: 'failed',
            conclusionMessage: defineMessage({
              id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":"failed"}',
              defaultMessage: 'Trade failed',
            }),
            conclusionTimestamp: event?.dateModified,
          });
          break;
      }
    } else {
      switch (event?.stage) {
        case 'creating':
          requiresAction = true;
          if (multisig?.isMultisig) {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":false,"stage":"creating","multisigState":true}',
                defaultMessage: 'New trade proposal',
              });
          } else {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":false,"stage":"creating","multisigState":false}',
                defaultMessage: 'New trade offer',
              });
          }
          timeline = this.makeTimelineForTransactions({
            multisigState: multisig,
            transactions: event?.transactions,
            nextEvents: [
              {
                formatDescription: ({ intl }) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"trade","terminationReason":false,"stage":"creating"}',
                    defaultMessage:
                      'Both parties must sign to execute the trade',
                  }),
              },
            ],
          });
          break;
        case 'connecting':
          pendingOthers = true;
          discard = this.makeCancelProposalCallback(profileId, event);
          formatDescription = (intl) =>
            intl.formatMessage({
              id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":false,"stage":"connecting"}',
              defaultMessage: 'Preparing new trade',
            });
          timeline = this.makeTimelineForTransactions({
            multisigState: multisig,
            transactions: event?.transactions,
            nextEvents: [
              {
                formatDescription: ({ intl }) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"trade","terminationReason":false,"stage":"connecting"}',
                    defaultMessage:
                      'Both parties must sign to execute the trade',
                  }),
              },
            ],
          });
          break;
        case 'confirming':
          requiresAction = true;
          resumeConfirm = this.makeResumeConfirmCallback(profileId, event);
          discard = this.makeCancelProposalCallback(profileId, event);
          if (multisig?.isMultisig) {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":false,"stage":"confirming","multisigState":true}',
                defaultMessage: 'Confirm prepared trade proposal',
              });
          } else {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":false,"stage":"confirming","multisigState":false}',
                defaultMessage: 'Confirm prepared trade',
              });
          }
          timeline = this.makeTimelineForTransactions({
            multisigState: multisig,
            transactions: event?.transactions,
            nextEvents: [
              {
                formatDescription: ({ intl }) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"trade","terminationReason":false,"stage":"confirming"}',
                    defaultMessage:
                      'Both parties must sign to execute the trade',
                  }),
              },
            ],
          });
          break;
        case 'proposal':
          if (multisig?.isMultisig) {
            switch (multisig.multisigState) {
              case 'proposed':
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":false,"stage":"proposal","multisigState":"proposed"}',
                    defaultMessage: 'Trade proposal under review',
                  });
                break;
              case 'review':
              default:
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":false,"stage":"proposal","multisigState":["review", true]}',
                    defaultMessage: 'Review trade proposal',
                  });
                break;
              case 'rejected':
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":false,"stage":"proposal","multisigState":"rejected"}',
                    defaultMessage: 'Rejected trade proposal under review',
                  });
                break;
              case 'signed':
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":false,"stage":"proposal","multisigState":"signed"}',
                    defaultMessage: 'Approved trade proposal under review',
                  });
                break;
            }
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              nextEvents: [
                {
                  formatDescription: ({ intl }) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"trade","terminationReason":false,"stage":"proposal"}',
                      defaultMessage:
                        'Both parties must sign to execute the trade',
                    }),
                },
              ],
            });
          } else {
            pendingOthers = true;
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":false,"stage":"proposal","multisigState":false}',
                defaultMessage: 'Sending trade offer…',
              });
            counterpartyVerb = 'to';
            timeline = this.makeTimelineForTransactions({
              transactions: event?.transactions,
              conclusionEvent: 'progress',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":false,"stage":"proposal","multisigState":false}',
                defaultMessage: 'Trade started',
              }),
              conclusionTimestamp: event?.dateCreated,
              isProcessing: true,
              nextEvents: [
                {
                  formatDescription: ({ intl }) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"trade","terminationReason":false,"stage":"proposal"}',
                      defaultMessage:
                        'Both parties must sign to execute the trade',
                    }),
                },
              ],
            });
          }
          break;
        case 'reviewing':
          if (isInitiator) {
            pendingOthers = true;
            if (!multisig?.isMultisig) {
              revokeTrade = this.makeRejectCallback(profileId, event);
            }
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":false,"stage":"reviewing","isInitiator":true}',
                defaultMessage: 'Trade offer sent, awaiting response',
              });
            counterpartyVerb = 'to';
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'next',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":false,"stage":"reviewing","isInitiator":true}',
                defaultMessage: 'Offer sent',
              }),
              nextEvents: [
                {
                  status: 'progress',
                  formatDescription: ({ intl }) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"trade","terminationReason":false,"stage":"reviewing","isInitiator":true}',
                      defaultMessage: 'Pending offer acceptance',
                    }),
                },
              ],
            });
          } else {
            counterpartyVerb = 'from';
            if (multisig?.isMultisig) {
              switch (multisig.multisigState) {
                case 'review':
                default:
                  formatDescription = (intl) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":false,"stage":"reviewing","isInitiator":false,"multisigState":["review", true]}',
                      defaultMessage: 'Review trade offer',
                    });
                  break;
                case 'rejected':
                  formatDescription = (intl) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":false,"stage":"reviewing","isInitiator":false,"multisigState":"rejected"}',
                      defaultMessage: 'Rejected trade offer under review',
                    });
                  break;
                case 'signed':
                  formatDescription = (intl) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":false,"stage":"reviewing","isInitiator":false,"multisigState":"signed"}',
                      defaultMessage: 'Approved trade offer under review',
                    });
                  break;
              }
            } else {
              requiresAction = true;
              const approveCallback = this.makeApproveCallback(
                profileId,
                event,
              );
              executeTrade = approveCallback;
              declineTrade = this.makeRejectCallback(profileId, event);
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":false,"stage":"reviewing","isInitiator":false,"multisigState":false}',
                  defaultMessage: 'Review trade offer',
                });
            }
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              previousEvents: [
                {
                  status: 'next',
                  formatDescription: ({ intl }) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":false,"stage":"reviewing","isInitiator":false}',
                      defaultMessage: 'Offer received',
                    }),
                  timestamp: event?.dateCreated,
                },
              ],
              transactions: event?.transactions,
              multisigFallbackTransaction: {
                signers: lockbox?.signers,
              },
              nextEvents: [
                {
                  status: multisig?.isMultisig ? 'pending' : 'progress',
                  formatDescription: ({ intl }) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.2{"type":"trade","terminationReason":false,"stage":"reviewing","isInitiator":false}',
                      defaultMessage: 'Ready to execute',
                    }),
                },
              ],
            });
          }
          break;
        case 'accepting':
        case 'finalizing':
        case 'executed':
        default:
          if (event?.stage === 'executed') {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":false,"stage":"executed"}',
                defaultMessage: 'Instruments traded',
              });
          } else {
            pendingOthers = true;
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"trade","terminationReason":false,"stage":["accepting","finalizing"]}',
                defaultMessage: 'Trading instruments…',
              });
          }
          if (isInitiator) {
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              insertBeforeConclusion: [
                {
                  status: 'next',
                  formatDescription: ({ intl }) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":false,"stage":["accepting","finalizing","executed"],"isInitiator":true}',
                      defaultMessage: 'Offer sent',
                    }),
                },
                {
                  status: 'completed',
                  formatDescription: ({ intl }) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"trade","terminationReason":false,"stage":["accepting","finalizing","executed"]}',
                      defaultMessage: 'Offer accepted',
                    }),
                },
              ],
              conclusionEvent:
                event?.stage === 'executed' ? 'succeeded' : 'progress',
              conclusionMessage:
                event?.stage === 'executed'
                  ? defineMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.2{"type":"trade","terminationReason":false,"stage":"executed"}',
                      defaultMessage: 'Trade executed',
                    })
                  : defineMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.2{"type":"trade","terminationReason":false,"stage":["accepting","finalizing"]}',
                      defaultMessage: 'Awaiting completion',
                    }),
              conclusionTimestamp: event?.dateModified,
              isProcessing: event?.stage !== 'executed',
            });
          } else {
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              previousEvents: [
                {
                  status: 'next',
                  formatDescription: ({ intl }) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"trade","terminationReason":false,"stage":["accepting","finalizing","executed"],"isInitiator":false}',
                      defaultMessage: 'Offer received',
                    }),
                  timestamp: event?.dateCreated,
                },
              ],
              transactions: event?.transactions,
              insertBeforeConclusion: [
                {
                  status: 'completed',
                  formatDescription: ({ intl }) =>
                    intl.formatMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.1{"type":"trade","terminationReason":false,"stage":["accepting","finalizing","executed"]}',
                      defaultMessage: 'Offer accepted',
                    }),
                },
              ],
              conclusionEvent:
                event?.stage === 'executed' ? 'succeeded' : 'progress',
              conclusionMessage:
                event?.stage === 'executed'
                  ? defineMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.2{"type":"trade","terminationReason":false,"stage":"executed"}',
                      defaultMessage: 'Trade executed',
                    })
                  : defineMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.2{"type":"trade","terminationReason":false,"stage":["accepting","finalizing"]}',
                      defaultMessage: 'Awaiting completion',
                    }),
              conclusionTimestamp: event?.dateModified,
              isProcessing: event?.stage !== 'executed',
            });
          }
          break;
      }
    }

    const formatted = {
      formatDescription,
      formatCounterparty: this.makeFormatEntityDescription(
        counterparty,
        counterpartyVerb,
      ),
      formatTradeAction: this.makeFormatTransferDescription(
        isInitiator ? transferSend : transferReceive,
      ),
      formatTradeResponse: this.makeFormatTransferDescription(
        isInitiator ? transferReceive : transferSend,
      ),
    };
    const transformedEvent = {
      ...event,
      ...multisig,
      timeline,
      formatted,
      requiresAction,
      pendingOthers,
      executeTrade,
      declineTrade,
      revokeTrade,
      resumeConfirm,
      discard,
    };
    if (event?.counterparties) {
      transformedEvent.counterparties = event.counterparties.map((party) => ({
        ...party,
        format: this.makeFormatEntityDescription(party, counterpartyVerb, true),
        transfers: party.transfers && [
          // rearrange the transfers so that they’re in the same order
          // as the action/response wording (“offered A for B”, “received B for A”, etc)
          ...party.transfers
            .filter(
              (transfer) =>
                transfer.direction === (isInitiator ? 'sent' : 'received'),
            )
            .map((transfer) => ({
              ...transfer,
              format: this.makeFormatTransferDescription(
                isInitiator ? transferSend : transferReceive,
              ),
            })),
          ...party.transfers
            .filter(
              (transfer) =>
                transfer.direction === (isInitiator ? 'received' : 'sent'),
            )
            .map((transfer) => ({
              ...transfer,
              format: this.makeFormatTransferDescription(
                isInitiator ? transferReceive : transferSend,
              ),
            })),
          ...party.transfers.filter(
            (transfer) =>
              transfer.direction !== 'sent' &&
              transfer.direction !== 'received',
          ),
        ],
      }));
    }
    return transformedEvent;
  }

  /** @internal */
  transformContractCreate(event, { profileId, lockboxes, ownHandle }) {
    const contractName = event?.contract?.name;

    let requiresAction = false;
    let pendingOthers = false;
    let formatDescription;
    let resumeConfirm, discard;
    let timeline;

    const multisig = this.calculateMultisigState(event, {
      profileId,
      lockboxes,
      ownHandle,
    });
    if (multisig.isMultisig) {
      if (multisig.pendingOthers) {
        pendingOthers = true;
      }
      if (multisig.requiresAction) {
        requiresAction = true;
      }
    }

    if (event?.terminationReason) {
      switch (event.terminationReason) {
        case 'cancelled':
          if (multisig?.isMultisig) {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":"cancelled","multisigState":true}',
                defaultMessage: 'Cancelled contract proposal',
              });
          } else {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":"cancelled","multisigState":false}',
                defaultMessage: 'Cancelled contract execution',
              });
          }
          timeline = this.makeTimelineForTransactions({
            conclusionEvent: 'expired',
            conclusionMessage: defineMessage({
              id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_formation","terminationReason":"cancelled"}',
              defaultMessage: 'Confirmation cancelled',
            }),
            conclusionTimestamp: event?.dateModified,
          });
          break;
        case 'revoked':
          if (multisig?.isMultisig) {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":"revoked","multisigState":true}',
                defaultMessage: 'Contract proposal retracted',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'revoked',
              conclusionTimestamp: event?.dateModified,
            });
          } else {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":"revoked","multisigState":false}',
                defaultMessage: 'Contract execution revoked',
              });
            timeline = this.makeTimelineForTransactions({
              transactions: event?.transactions,
              conclusionEvent: 'revoked',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_formation","terminationReason":"revoked","multisigState":false}',
                defaultMessage: 'Execution retracted',
              }),
              conclusionTimestamp: event?.dateModified,
            });
          }
          break;
        case 'rejected':
          if (multisig?.isMultisig) {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":"rejected","multisigState":true}',
                defaultMessage: 'Contract proposal blocked',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'blocked',
            });
          } else {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":"rejected","multisigState":false}',
                defaultMessage: 'Contract execution rejected',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'failed',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_formation","terminationReason":"rejected","multisigState":false}',
                defaultMessage: 'Execution rejected',
              }),
              conclusionTimestamp: event?.dateModified,
            });
          }
          break;
        case 'expired':
          if (multisig?.isMultisig) {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":"expired","multisigState":true}',
                defaultMessage: 'Contract proposal expired',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'expired',
              conclusionTimestamp: event?.dateModified,
            });
          } else {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":"expired","multisigState":false}',
                defaultMessage: 'Contract execution expired',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'expired',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_formation","terminationReason":"expired","multisigState":false}',
                defaultMessage: 'Execution expired',
              }),
              conclusionTimestamp: event?.dateModified,
            });
          }
          break;
        case 'failed':
        default:
          formatDescription = (intl) =>
            intl.formatMessage({
              id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":"failed"}',
              defaultMessage: 'Contract execution failed',
            });
          timeline = this.makeTimelineForTransactions({
            multisigState: multisig,
            transactions: event?.transactions,
            conclusionEvent: 'failed',
            conclusionMessage: defineMessage({
              id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_formation","terminationReason":"failed"}',
              defaultMessage: 'Execution failed',
            }),
            conclusionTimestamp: event?.dateModified,
          });
          break;
      }
    } else {
      switch (event?.stage) {
        case 'creating':
        case 'connecting': // Shouldn’t happen
        case 'confirming':
          if (event.stage !== 'connecting') {
            requiresAction = true;
          } else {
            pendingOthers = true;
          }
          if (event?.stage === 'connecting') {
            discard = this.makeCancelProposalCallback(profileId, event);
          } else if (event?.stage === 'confirming') {
            resumeConfirm = this.makeResumeConfirmCallback(profileId, event);
            discard = this.makeCancelProposalCallback(profileId, event);
          }
          if (multisig?.isMultisig) {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":false,"stage":["creating","confirming"],"multisigState":true}',
                defaultMessage: 'Contract proposal',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
            });
          } else {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":false,"stage":["creating","confirming"],"multisigState":false}',
                defaultMessage: 'Contract execution',
              });
            timeline = [
              {
                status: 'progress',
                formatDescription: ({ intl }) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_formation","terminationReason":false,"stage":["creating","confirming"],"multisigState":false}',
                    defaultMessage: 'Ready for execution',
                  }),
              },
            ];
          }
          break;
        case 'proposal':
          if (multisig?.isMultisig) {
            switch (multisig.multisigState) {
              case 'proposed':
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":false,"stage":"proposal","multisigState":"proposed"}',
                    defaultMessage: 'Contract proposal under review',
                  });
                break;
              case 'review':
              default:
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":false,"stage":"proposal","multisigState":["review", true]}',
                    defaultMessage: 'Review contract proposal',
                  });
                break;
              case 'rejected':
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":false,"stage":"proposal","multisigState":"rejected"}',
                    defaultMessage: 'Rejected contract proposal under review',
                  });
                break;
              case 'signed':
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":false,"stage":"proposal","multisigState":"signed"}',
                    defaultMessage: 'Approved contract proposal under review',
                  });
                break;
            }
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
            });
          } else {
            pendingOthers = true;
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":false,"stage":"proposal","multisigState":false}',
                defaultMessage: 'Executing contract…',
              });
            timeline = this.makeTimelineForTransactions({
              transactions: event?.transactions,
              conclusionEvent: 'progress',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_formation","terminationReason":false,"stage":"proposal","multisigState":false}',
                defaultMessage: 'Executing started',
              }),
              conclusionTimestamp: event?.dateCreated,
              isProcessing: true,
            });
          }
          break;
        case 'accepting':
        case 'finalizing':
        case 'executed':
        default:
          if (event?.stage === 'executed') {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":false,"stage":"executed"}',
                defaultMessage: 'Contract executed',
              });
          } else {
            pendingOthers = true;
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_formation","terminationReason":false,"stage":["accepting","finalizing"]}',
                defaultMessage: 'Executing contract…',
              });
          }
          timeline = this.makeTimelineForTransactions({
            multisigState: multisig,
            transactions: event?.transactions,
            conclusionEvent:
              event?.stage === 'executed' ? 'succeeded' : 'progress',
            conclusionMessage:
              event?.stage === 'executed'
                ? defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_formation","terminationReason":false,"stage":"executed"}',
                    defaultMessage: 'Contract executed',
                  })
                : defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_formation","terminationReason":false,"stage":["accepting","finalizing"]}',
                    defaultMessage: 'Awaiting completion',
                  }),
            conclusionTimestamp: event?.dateModified,
            isProcessing: event?.stage !== 'executed',
          });
          break;
      }
    }

    const formatted = {
      formatDescription,
    };
    return {
      ...event,
      ...multisig,
      timeline,
      formatted,
      requiresAction,
      pendingOthers,
      resumeConfirm,
      discard,
      contract: {
        ...event?.contract,
        formatted: {
          description: contractName,
          descriptionParts: contractName
            ? [
                {
                  type: 'contract',
                  contractAddress: event?.contract?.contractAddress,
                  value: contractName,
                },
              ]
            : undefined,
        },
      },
      instruments: event?.instruments?.map((instrument) => ({
        ...instrument,
        formatted: {
          formatDescription: this.makeFormatTransferDescription(instrument),
        },
      })),
    };
  }

  /** @internal */
  transformContractAmend(event, { profileId, lockboxes, ownHandle }) {
    const contractName = event?.contract?.name;

    let requiresAction = false;
    let pendingOthers = false;
    let resumeConfirm, discard;
    let formatDescription;
    let timeline;

    const multisig = this.calculateMultisigState(event, {
      profileId,
      lockboxes,
      ownHandle,
    });
    if (multisig.isMultisig) {
      if (multisig.pendingOthers) {
        pendingOthers = true;
      }
      if (multisig.requiresAction) {
        requiresAction = true;
      }
    }

    if (event?.terminationReason) {
      switch (event.terminationReason) {
        case 'cancelled':
          if (multisig?.isMultisig) {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":"cancelled","multisigState":true}',
                defaultMessage: 'Cancelled amendment proposal',
              });
          } else {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":"cancelled","multisigState":false}',
                defaultMessage: 'Cancelled amendment execution',
              });
          }
          timeline = this.makeTimelineForTransactions({
            conclusionEvent: 'expired',
            conclusionMessage: defineMessage({
              id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_amendment","terminationReason":"cancelled"}',
              defaultMessage: 'Confirmation cancelled',
            }),
            conclusionTimestamp: event?.dateModified,
          });
          break;
        case 'revoked':
          if (multisig?.isMultisig) {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":"revoked","multisigState":true}',
                defaultMessage: 'Contract amendment proposal retracted',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'revoked',
              conclusionTimestamp: event?.dateModified,
            });
          } else {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":"revoked","multisigState":false}',
                defaultMessage: 'Contract amendment retracted',
              });
            timeline = this.makeTimelineForTransactions({
              transactions: event?.transactions,
              conclusionEvent: 'revoked',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_amendment","terminationReason":"revoked","multisigState":false}',
                defaultMessage: 'Amendment retracted',
              }),
              conclusionTimestamp: event?.dateModified,
            });
          }
          break;
        case 'rejected':
          if (multisig?.isMultisig) {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":"rejected","multisigState":true}',
                defaultMessage: 'Contract amendment proposal blocked',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'blocked',
            });
          } else {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":"rejected","multisigState":false}',
                defaultMessage: 'Contract amendment rejected',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'failed',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_amendment","terminationReason":"rejected","multisigState":false}',
                defaultMessage: 'Amendment rejected',
              }),
              conclusionTimestamp: event?.dateModified,
            });
          }
          break;
        case 'expired':
          if (multisig?.isMultisig) {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":"expired","multisigState":true}',
                defaultMessage: 'Contract amendment proposal expired',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'expired',
              conclusionTimestamp: event?.dateModified,
            });
          } else {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":"expired","multisigState":false}',
                defaultMessage: 'Contract amendment expired',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'expired',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_amendment","terminationReason":"expired","multisigState":false}',
                defaultMessage: 'Amendment expired',
              }),
              conclusionTimestamp: event?.dateModified,
            });
          }
          break;
        case 'failed':
        default:
          formatDescription = (intl) =>
            intl.formatMessage({
              id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":"failed"}',
              defaultMessage: 'Contract amendment failed',
            });
          timeline = this.makeTimelineForTransactions({
            multisigState: multisig,
            transactions: event?.transactions,
            conclusionEvent: 'failed',
            conclusionMessage: defineMessage({
              id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_amendment","terminationReason":"failed"}',
              defaultMessage: 'Amendment failed',
            }),
            conclusionTimestamp: event?.dateModified,
          });
          break;
      }
    } else {
      let stage = event?.stage;
      if (stage === 'confirming' && multisig?.isMultisig) {
        // Workaround for current change-of-organization-admin ammendments,
        // which have stage=confirming when it should be proposal
        stage = 'proposal';
      }

      switch (stage) {
        case 'creating':
        case 'connecting': // Shouldn’t happen
        case 'confirming':
          if (event.stage !== 'connecting') {
            requiresAction = true;
          } else {
            pendingOthers = true;
          }
          if (event?.stage === 'connecting') {
            discard = this.makeCancelProposalCallback(profileId, event);
          } else if (event?.stage === 'confirming') {
            resumeConfirm = this.makeResumeConfirmCallback(profileId, event);
            discard = this.makeCancelProposalCallback(profileId, event);
          }
          if (multisig?.isMultisig) {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":false,"stage":["creating","confirming"],"multisigState":true}',
                defaultMessage: 'Contract amendment proposal',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
            });
          } else {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":false,"stage":["creating","confirming"],"multisigState":false}',
                defaultMessage: 'Contract amendment',
              });
            timeline = [
              {
                status: 'progress',
                formatDescription: ({ intl }) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_amendment","terminationReason":false,"stage":["creating","confirming"],"multisigState":false}',
                    defaultMessage: 'Ready to amend',
                  }),
              },
            ];
          }
          break;
        case 'proposal':
          if (multisig?.isMultisig) {
            switch (multisig.multisigState) {
              case 'proposed':
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":false,"stage":"proposal","multisigState":"proposed"}',
                    defaultMessage: 'Contract amendment under review',
                  });
                break;
              case 'review':
              default:
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":false,"stage":"proposal","multisigState":["review", true]}',
                    defaultMessage: 'Review contract amendment proposal',
                  });
                break;
              case 'rejected':
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":false,"stage":"proposal","multisigState":"rejected"}',
                    defaultMessage: 'Rejected contract amendment under review',
                  });
                break;
              case 'signed':
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":false,"stage":"proposal","multisigState":"signed"}',
                    defaultMessage: 'Approved contract amendment under review',
                  });
                break;
            }
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
            });
          } else {
            pendingOthers = true;
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":false,"stage":"proposal","multisigState":false}',
                defaultMessage: 'Amending contract…',
              });
            timeline = this.makeTimelineForTransactions({
              transactions: event?.transactions,
              conclusionEvent: 'progress',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_amendment","terminationReason":false,"stage":"proposal","multisigState":false}',
                defaultMessage: 'Amendment started',
              }),
              conclusionTimestamp: event?.dateCreated,
              isProcessing: true,
            });
          }
          break;
        case 'accepting':
        case 'finalizing':
        case 'executed':
        default:
          if (event?.stage === 'executed') {
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":false,"stage":"executed"}',
                defaultMessage: 'Contract amended',
              });
          } else {
            pendingOthers = true;
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_amendment","terminationReason":false,"stage":["accepting","finalizing"]}',
                defaultMessage: 'Amending contract…',
              });
          }
          timeline = this.makeTimelineForTransactions({
            multisigState: multisig,
            transactions: event?.transactions,
            conclusionEvent:
              event?.stage === 'executed' ? 'succeeded' : 'progress',
            conclusionMessage:
              event?.stage === 'executed'
                ? defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_amendment","terminationReason":false,"stage":"executed"}',
                    defaultMessage: 'Contract executed',
                  })
                : defineMessage({
                    id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_amendment","terminationReason":false,"stage":["accepting","finalizing"]}',
                    defaultMessage: 'Awaiting completion',
                  }),
            conclusionTimestamp: event?.dateModified,
            isProcessing: event?.stage !== 'executed',
          });
          break;
      }
    }

    const formatted = {
      formatDescription,
    };
    return {
      ...event,
      ...multisig,
      timeline,
      formatted,
      requiresAction,
      pendingOthers,
      resumeConfirm,
      discard,
      contract: {
        ...event?.contract,
        formatted: {
          description: contractName,
          descriptionParts: contractName
            ? [
                {
                  type: 'contract',
                  contractAddress: event?.contract?.contractAddress,
                  value: contractName,
                },
              ]
            : undefined,
        },
      },
      instruments: event?.instruments?.map((instrument) => ({
        ...instrument,
        formatted: {
          formatDescription: this.makeFormatTransferDescription(instrument),
        },
      })),
    };
  }

  /** @internal */
  transformContractExpire(event) {
    const contractName = event?.contract?.name;

    let formatDescription;
    let timeline;

    if (event?.terminationReason) {
      formatDescription = (intl) =>
        intl.formatMessage({
          id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_expire","terminationReason":true}',
          defaultMessage: 'Contract expiry failed',
        });
      timeline = [
        {
          status: 'failed',
          formatDescription: ({ intl }) =>
            intl.formatMessage({
              id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_expire","terminationReason":true}',
              defaultMessage: 'Expiry failed',
            }),
          timestamp: event?.dateModified,
        },
      ];
    } else {
      formatDescription = (intl) =>
        intl.formatMessage({
          id: 'TokenizedSDK:ActivityEvent.summary{"type":"contract_expire","terminationReason":false}',
          defaultMessage: 'Contract ended',
        });
      timeline = [
        {
          status: 'expired',
          formatDescription: ({ intl }) =>
            intl.formatMessage({
              id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"contract_expire","terminationReason":false}',
              defaultMessage: 'Contract ended',
            }),
          isProcessing: event?.stage !== 'executed',
          timestamp: event?.dateModified,
        },
      ];
    }

    const formatted = {
      formatDescription,
    };
    return {
      ...event,
      formatted,
      timeline,
      requiresAction: false,
      pendingOthers: false,
      contract: {
        ...event?.contract,
        formatted: {
          description: contractName,
          descriptionParts: contractName
            ? [
                {
                  type: 'contract',
                  contractAddress: event?.contract?.contractAddress,
                  value: contractName,
                },
              ]
            : undefined,
        },
      },
      instruments: event.instruments?.map((instrument) => ({
        ...instrument,
        ...(instrument.total?.quantity > 0
          ? {
              delta: {
                instrumentId: instrument.instrumentId,
                quantity: -instrument.total.quantity,
              },
            }
          : {}),
        formatted: {
          formatDescription: this.makeFormatTransferDescription(instrument),
        },
      })),
    };
  }

  /** @internal */
  transformInstrumentExpire(event) {
    const contractName = event?.contract?.name;

    let formatDescription;
    let timeline;

    if (event?.terminationReason) {
      formatDescription = (intl) =>
        intl.formatMessage({
          id: 'TokenizedSDK:ActivityEvent.summary{"type":"instrument_expire","terminationReason":true}',
          defaultMessage: 'Instrument expiry failed',
        });
      timeline = [
        {
          status: 'failed',
          formatDescription: ({ intl }) =>
            intl.formatMessage({
              id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"instrument_expire","terminationReason":true}',
              defaultMessage: 'Expiry failed',
            }),
          timestamp: event?.dateModified,
        },
      ];
    } else {
      formatDescription = (intl) =>
        intl.formatMessage({
          id: 'TokenizedSDK:ActivityEvent.summary{"type":"instrument_expire","terminationReason":false}',
          defaultMessage: 'Instruments expired',
        });
      timeline = [
        {
          status: 'expired',
          formatDescription: ({ intl }) =>
            intl.formatMessage({
              id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"instrument_expire","terminationReason":false}',
              defaultMessage: 'Instruments expired',
            }),
          isProcessing: event?.stage === 'executed',
          timestamp: event?.dateModified,
        },
      ];
    }

    const formatted = {
      formatDescription,
    };
    const transformedEvent = {
      ...event,
      formatted,
      timeline,
      requiresAction: false,
      pendingOthers: false,
      contract: {
        ...event?.contract,
        formatted: {
          description: contractName,
          descriptionParts: contractName
            ? [
                {
                  type: 'contract',
                  contractAddress: event?.contract?.contractAddress,
                  value: contractName,
                },
              ]
            : undefined,
        },
      },
      instruments: event.instruments?.map((instrument) => ({
        ...instrument,
        ...(instrument.total?.quantity > 0
          ? {
              delta: {
                instrumentId: instrument.instrumentId,
                quantity: -instrument.total.quantity,
              },
            }
          : {}),
        formatted: {
          formatDescription: this.makeFormatTransferDescription(instrument),
        },
      })),
    };
    return transformedEvent;
  }

  /** @internal */
  transformReorg(event, { profileId, entityId, lockboxes, ownHandle }) {
    const lockboxId = event?.lockboxId;
    const lockbox = event?.lockbox;
    const signers = lockbox?.signers || [];
    const stage = event?.stage;

    const entity = lockbox?.entityHandle
      ? {
          displayName: lockbox?.entityName,
          displayHandle: lockbox?.entityHandle,
          isInternal: true,
          isVerified: true,
          isAnonymous: false,
        }
      : {};
    const isEventForThisEntity = !!entityId && lockbox?.entityId === entityId;

    const formerLockboxes = lockboxes?.lockboxes
      .filter(({ id }) => event.lockbox?.replaces?.includes(id))
      .map((formerLockbox) => ({
        ...formerLockbox,
        formatted: {
          formatControlArrangement:
            this.makeFormatControlArrangement(formerLockbox),
        },
      }));

    const contractName = event?.contract?.name;

    let multisig;
    let requiresAction = false;
    let pendingOthers = false;
    let formatDescription;
    let timeline;

    if (event?.reorgStage === 'lockbox_creation') {
      const numberOfAdministrators = lockbox?.signers?.length || 1;
      const requiredSigners = lockbox?.requiredSigners || 1;
      const proposer = signers.find((signer) => signer?.isProposer);
      const proposerHandle = proposer?.handle;
      const isProposer =
        (lockbox?.signers || []).some(
          (signer) =>
            signer?.isProposer && ownHandle && signer?.handle === ownHandle,
        ) || numberOfAdministrators === 1;
      const invitationsSent = stage !== 'creating' && stage !== 'confirming';
      const numberOfAcceptedAdministrators = signers.filter(
        (signer) => signer?.dateAccepted,
      ).length;
      const readyToActivate =
        invitationsSent &&
        numberOfAdministrators - numberOfAcceptedAdministrators === 1;
      const haveResponded =
        stage === 'accepting' || stage === 'finalizing' || stage === 'executed'
          ? true
          : signers.some(
              (signer) =>
                ownHandle &&
                signer?.handle === ownHandle &&
                (signer?.dateAccepted || signer?.dateRejected),
            );
      const multisigStateForTimeline = {
        isMultisig: true,
        invitationsSent,
        signaturesRemaining:
          numberOfAdministrators - numberOfAcceptedAdministrators,
        signatureCount: numberOfAcceptedAdministrators,
      };

      if (isEventForThisEntity) {
        if (event?.terminationReason) {
          formatDescription = (intl) =>
            intl.formatMessage({
              id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"lockboxCreate","isEventForThisEntity":true,"terminationReason":true}',
              defaultMessage: 'Workspace modification failed',
            });
        } else {
          switch (stage) {
            case 'creating':
            case 'confirming':
            case 'proposal':
              pendingOthers = true;
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"lockboxCreate","isEventForThisEntity":true,"terminationReason":false,"stage":"proposal"}',
                  defaultMessage: 'Workspace modification, pending acceptance',
                });
              break;
            case 'executed':
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"lockboxCreate","isEventForThisEntity":true,"terminationReason":false,"stage":"executed"}',
                  defaultMessage: 'Workspace modification approved',
                });
              break;
            case 'connecting':
            case 'accepting':
            case 'finalizing':
            default:
              pendingOthers = true;
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"lockboxCreate","isEventForThisEntity":true,"terminationReason":false}',
                  defaultMessage: 'Approving workspace modification…',
                });
              break;
          }
        }
      } else {
        if (event?.terminationReason) {
          switch (event.terminationReason) {
            case 'cancelled':
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"lockboxCreate","isEventForThisEntity":false,"terminationReason":"cancelled"}',
                  defaultMessage: 'Cancelled workspace modification',
                });
              break;
            case 'revoked':
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"lockboxCreate","isEventForThisEntity":false,"terminationReason":"revoked"}',
                  defaultMessage: 'Workspace modification revoked',
                });
              break;
            case 'rejected':
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"lockboxCreate","isEventForThisEntity":false,"terminationReason":"rejected"}',
                  defaultMessage: 'Workspace modification blocked',
                });
              break;
            case 'expired':
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"lockboxCreate","isEventForThisEntity":false,"terminationReason":"expired"}',
                  defaultMessage: 'Workspace modification expired',
                });
              break;
            case 'failed':
            default:
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"lockboxCreate","isEventForThisEntity":false,"terminationReason":true}',
                  defaultMessage: 'Workspace modification failed',
                });
              break;
          }
        } else {
          switch (stage) {
            case 'creating':
              requiresAction = true;
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"lockboxCreate","isEventForThisEntity":false,"terminationReason":false,"stage":"creating"}',
                  defaultMessage: 'Workspace modification, review details',
                });
              break;
            case 'confirming':
              requiresAction = true;
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"lockboxCreate","isEventForThisEntity":false,"terminationReason":false,"stage":"confirming"}',
                  defaultMessage: 'Workspace modification',
                });
              break;
            case 'proposal':
              if (haveResponded) {
                pendingOthers = true;
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"lockboxCreate","isEventForThisEntity":false,"terminationReason":false,"stage":"proposal","haveResponded":true}',
                    defaultMessage:
                      'Workspace modification, pending acceptance',
                  });
              } else {
                requiresAction = true;
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"lockboxCreate","isEventForThisEntity":false,"terminationReason":false,"stage":"proposal","haveResponded":false}',
                    defaultMessage: 'Workspace modification',
                  });
              }
              break;
            case 'executed':
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"lockboxCreate","isEventForThisEntity":false,"terminationReason":false,"stage":"executed","isProposer":false}',
                  defaultMessage: 'Workspace modification approved',
                });
              break;
            case 'connecting':
            case 'accepting':
            case 'finalizing':
            default:
              pendingOthers = true;
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"lockboxCreate","isEventForThisEntity":false,"terminationReason":false}',
                  defaultMessage: 'Approving workspace modification…',
                });
              break;
          }
        }
      }

      timeline = [];
      const timelineProposalEvent =
        proposer?.handle && invitationsSent && numberOfAdministrators > 1
          ? [
              {
                status: 'next',
                handle: proposer.handle,
                formatDescription: ({ intl, name }) =>
                  intl.formatMessage(this.multisigTimelineMessages.proposed, {
                    name,
                  }),
                timestamp: event?.dateCreated,
              },
            ]
          : undefined;

      if (event?.terminationReason) {
        switch (event.terminationReason) {
          case 'cancelled':
            timeline = this.makeTimelineForTransactions({
              conclusionEvent: 'expired',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"lockboxCreate","terminationReason":"cancelled"}',
                defaultMessage: 'Cancelled',
              }),
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'revoked':
            timeline = this.makeTimelineForTransactions({
              previousEvents: timelineProposalEvent,
              multisigState: multisigStateForTimeline,
              workspaceInvitationProgress: signers,
              conclusionEvent: 'revoked',
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'rejected':
            timeline = this.makeTimelineForTransactions({
              previousEvents: timelineProposalEvent,
              multisigState: multisigStateForTimeline,
              workspaceInvitationProgress: signers,
              conclusionEvent: 'blocked',
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'expired':
            timeline = this.makeTimelineForTransactions({
              previousEvents: timelineProposalEvent,
              multisigState: multisigStateForTimeline,
              workspaceInvitationProgress: signers,
              conclusionEvent: 'expired',
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'failed':
          default:
            timeline = this.makeTimelineForTransactions({
              previousEvents: timelineProposalEvent,
              multisigState: multisigStateForTimeline,
              workspaceInvitationProgress: signers,
              conclusionEvent: 'failed',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"lockboxCreate","terminationReason":"failed"}',
                defaultMessage: 'Proposal failed',
              }),
              conclusionTimestamp: event?.dateModified,
            });
            break;
        }
      } else {
        timeline = this.makeTimelineForTransactions({
          previousEvents: timelineProposalEvent,
          multisigState: multisigStateForTimeline,
          workspaceInvitationProgress: signers,
        });
      }

      const accept = async (setMfaRequest) => {
        let { handshakeId } =
          await this.tokenizedApi.transfers.acceptLockboxProposal(
            profileId,
            lockboxId,
          );
        if (handshakeId) {
          await this.tokenizedApi.mfa.waitForMfa(handshakeId, setMfaRequest);
        }

        this.tokenizedApi.reloadAll();
      };
      const reject = async () => {
        await this.tokenizedApi.transfers.rejectLockboxProposal(
          profileId,
          lockboxId,
        );
      };
      let actions = {};
      if (requiresAction) {
        actions = { accept, reject };
      } else if (isProposer && pendingOthers && !haveResponded) {
        // Proposers can retract the proposal at any time
        actions = { reject };
      }

      multisig = {
        ...multisigStateForTimeline,
        numberOfAdministrators,
        requiredSigners,
        isProposer,
        proposerHandle,
        numberOfAcceptedAdministrators,
        readyToActivate,
        haveResponded,
        ...actions,
      };
    } else {
      multisig = this.calculateMultisigState(event, {
        profileId,
        lockboxes,
        ownHandle,
      });
      if (multisig.isMultisig) {
        if (multisig.pendingOthers) {
          pendingOthers = true;
        }
        if (multisig.requiresAction) {
          requiresAction = true;
        }
      }

      if (event?.terminationReason) {
        switch (event.terminationReason) {
          case 'cancelled':
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"migration","terminationReason":"cancelled"}',
                defaultMessage: 'Cancelled workspace modification',
              });
            timeline = this.makeTimelineForTransactions({
              conclusionEvent: 'expired',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"reorg","reorgStage":"migration","terminationReason":"cancelled"}',
                defaultMessage: 'Confirmation cancelled',
              }),
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'revoked':
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"migration","terminationReason":"revoked"}',
                defaultMessage: 'Workspace modification cancelled',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'revoked',
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'rejected':
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"migration","terminationReason":"rejected"}',
                defaultMessage: 'Workspace modification blocked',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'blocked',
            });
            break;
          case 'expired':
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"migration","terminationReason":"expired"}',
                defaultMessage: 'Workspace modification expired',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'expired',
              conclusionTimestamp: event?.dateModified,
            });
            break;
          case 'failed':
          default:
            formatDescription = (intl) =>
              intl.formatMessage({
                id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"migration","terminationReason":"failed"}',
                defaultMessage: 'Workspace modification failed',
              });
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent: 'failed',
              conclusionMessage: defineMessage({
                id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"reorg","reorgStage":"migration","terminationReason":"failed"}',
                defaultMessage: 'Modification failed',
              }),
              conclusionTimestamp: event?.dateModified,
            });
            break;
        }
      } else {
        let stage = event?.stage;
        if (stage === 'confirming' && multisig?.isMultisig) {
          // Workaround for current change-of-organization-admin ammendments,
          // which have stage=confirming when it should be proposal
          stage = 'proposal';
        }

        switch (stage) {
          // A workspace modification migration activity is automatically
          // created in the “proposal stage” (awaiting admin signatures),
          // so if we see any of the earlier stages, just treat them as proposal
          case 'creating':
          case 'connecting':
          case 'confirming':
          case 'proposal':
            switch (multisig.multisigState) {
              case 'proposed':
              case 'rejected':
              case 'signed':
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"migration","terminationReason":false,"stage":"proposal","haveResponded":true}',
                    defaultMessage:
                      'Workspace modification, awaiting migration',
                  });
                break;
              case 'review':
              default:
                formatDescription = (intl) =>
                  intl.formatMessage({
                    id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"migration","terminationReason":false,"stage":"proposal","haveResponded":false}',
                    defaultMessage: 'Workspace modification, approve migration',
                  });
                break;
            }
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
            });
            break;
          case 'accepting':
          case 'finalizing':
          case 'executed':
          default:
            if (event?.stage === 'executed') {
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"migration","terminationReason":false,"stage":"executed"}',
                  defaultMessage: 'Workspace modified',
                });
            } else {
              pendingOthers = true;
              formatDescription = (intl) =>
                intl.formatMessage({
                  id: 'TokenizedSDK:ActivityEvent.summary{"type":"reorg","reorgStage":"migration","terminationReason":false,"stage":["accepting","finalizing"]}',
                  defaultMessage: 'Modifying workspace…',
                });
            }
            timeline = this.makeTimelineForTransactions({
              multisigState: multisig,
              transactions: event?.transactions,
              conclusionEvent:
                event?.stage === 'executed' ? 'succeeded' : 'progress',
              conclusionMessage:
                event?.stage === 'executed'
                  ? defineMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"reorg","reorgStage":"migration","terminationReason":false,"stage":"executed"}',
                      defaultMessage: 'Workspace modified',
                    })
                  : defineMessage({
                      id: 'TokenizedSDK:ActivityEvent.timeline.0{"type":"reorg","reorgStage":"migration","terminationReason":false,"stage":["accepting","finalizing"]}',
                      defaultMessage: 'Awaiting completion',
                    }),
              conclusionTimestamp: event?.dateModified,
              isProcessing: event?.stage !== 'executed',
            });
            break;
        }
      }
    }

    return {
      ...event,
      ...multisig,
      timeline,
      requiresAction,
      pendingOthers,
      isEventForThisEntity,
      entity: {
        ...entity,
        format: this.makeFormatEntityDescription(entity, null),
      },
      lockbox: lockbox && {
        ...lockbox,
        formatted: {
          formatControlArrangement: this.makeFormatControlArrangement(lockbox),
        },
      },
      formerLockboxes,
      contract: {
        ...event?.contract,
        formatted: {
          description: contractName,
          descriptionParts: contractName
            ? [
                {
                  type: 'contract',
                  contractAddress: event?.contract?.contractAddress,
                  value: contractName,
                },
              ]
            : undefined,
        },
      },
      instruments: event?.instruments?.map((instrument) => ({
        ...instrument,
        formatted: {
          formatDescription: this.makeFormatTransferDescription(instrument),
        },
      })),
      formatted: {
        formatDescription,
        formatCounterparty: this.makeFormatEntityDescription(entity, 'for'),
      },
    };
  }

  /** @internal */
  isSameTransaction(a, b) {
    if (!a?.signers || !b?.signers) {
      return false;
    }
    return (
      a.signers
        .map(({ id }) => id || '')
        .sort()
        .join() ===
      b.signers
        .map(({ id }) => id || '')
        .sort()
        .join()
    );
  }

  /** @internal */
  transformEvent(event, context) {
    let mappedEventType = event?.activityEventType;
    switch (event?.activityEventType) {
      case 'contract_formation':
      case 'instrument_creation':
        mappedEventType = 'contract_formation';
        break;
      case 'contract_amendment':
      case 'instrument_amendment':
        mappedEventType = 'contract_amendment';
        break;
      default:
        break;
    }

    const fees = {
      toIssuers: {
        quantity: event?.fees?.transferFees || 0,
        instrumentId: CURRENCY_BSV,
      },
      toAgent: {
        quantity: event?.fees?.agentFees || 0,
        instrumentId: CURRENCY_BSV,
      },
      toNetwork: {
        quantity: event?.fees?.miningFees || 0,
        instrumentId: CURRENCY_BSV,
      },
      toTokenAdmin: {
        quantity: event?.fees?.dustNet || 0,
        instrumentId: CURRENCY_BSV,
      },
      fromTransfers: {
        quantity: 0,
        instrumentId: CURRENCY_BSV,
      },
      breakdown: [],
    };

    if (event?.fees?.agentFees) {
      fees.breakdown.push({
        type: 'actionFee',
        instrumentId: CURRENCY_BSV,
        quantity: -event?.fees?.agentFees || 0,
      });
    }
    if (event?.fees?.miningFees) {
      fees.breakdown.push({
        type: 'miningFee',
        instrumentId: CURRENCY_BSV,
        quantity: -event?.fees?.miningFees || 0,
      });
    }
    if (event?.fees?.dustNet) {
      fees.breakdown.push({
        type: 'tokenAdmin',
        instrumentId: CURRENCY_BSV,
        quantity: -event?.fees?.dustNet || 0,
      });
    }
    if (event?.fees?.transferFees) {
      fees.breakdown.push({
        type: 'transferFee',
        instrumentId: CURRENCY_BSV,
        quantity: -event?.fees?.transferFees || 0,
      });
    }

    event?.fees?.inInstruments?.forEach?.(
      ({ feeType, instrumentId, quantity }) => {
        if (feeType === 'transfer' && quantity) {
          fees.breakdown.push({
            type: 'transferFee',
            instrumentId,
            quantity: -quantity,
          });
        }
      },
    );

    const keepTransferFeeAsPayment =
      event?.activityEventType === 'transfer_fee';
    if (!keepTransferFeeAsPayment) {
      for (const party of event?.counterparties || []) {
        for (const transfer of party?.transfers || []) {
          if (
            transfer?.feeType === 'transfer' &&
            transfer?.instrumentId &&
            transfer?.quantity
          ) {
            if (transfer.instrumentId === CURRENCY_BSV) {
              fees.fromTransfers.quantity +=
                transfer.direction === 'received'
                  ? transfer.quantity
                  : -transfer.quantity;
            }
            fees.breakdown.push({
              type: 'transferFeePayment',
              instrumentId: transfer.instrumentId,
              quantity:
                transfer.direction === 'received'
                  ? transfer.quantity
                  : -transfer.quantity,
            });
          }
        }
      }
    }

    fees.all = {
      quantity: fees.breakdown.reduce(
        (bsvTotal, { instrumentId, quantity }) =>
          bsvTotal + (instrumentId === CURRENCY_BSV ? quantity : 0),
        0,
      ),
      instrumentId: CURRENCY_BSV,
    };

    // Currently reorgs are covered by two activity events, first
    // a 'lock_box_creation_proposal' event where the new admins approve
    // creation of the new vault/lockbox, and once that is complete a
    // migration event, which can currently be a contract amendment or
    // a payment (if there are non-issued tokens to transfer). We’re expecting
    // to cover both events with a new unified activity type ('reorg'), which is
    // simulated here by identifying the current steps and transforming the data.
    const reorgAdditions = {};
    if (
      mappedEventType === 'lock_box_creation_proposal' &&
      event?.lockbox?.replaces?.length
    ) {
      // First step: new admins approve creation of the new vault/lockbox
      mappedEventType = 'reorg';
      reorgAdditions.reorgStage = 'lockbox_creation';
    } else if (
      event?.counterparties?.length === 2 &&
      event.counterparties[0].lockboxId &&
      event.counterparties[0].povLockboxId &&
      event.counterparties[0].lockboxId ===
        event.counterparties[1].povLockboxId &&
      event.counterparties[1].lockboxId === event.counterparties[0].povLockboxId
    ) {
      // Second step: migration of balances and contracts
      mappedEventType = 'reorg';
      reorgAdditions.reorgStage = 'migration';
      // transformReorg expects to see the new lockbox in the activity data
      const reorgLockboxes = [
        context.lockboxes?.idMap?.get(event.counterparties[0].lockboxId),
        context.lockboxes?.idMap?.get(event.counterparties[1].lockboxId),
      ];
      reorgAdditions.lockbox =
        reorgLockboxes[1]?.replaces?.[0] &&
        reorgLockboxes[1].replaces[0] === reorgLockboxes[0].id
          ? reorgLockboxes[1]
          : reorgLockboxes[0];
    }

    const mappedEvent = {
      ...event,
      activityEventType: mappedEventType,
      ...reorgAdditions,
      profileId: context.profileId,
      counterparties: event?.counterparties?.map?.(
        ({ transfers, ...party }) => ({
          ...party,
          transfers: transfers
            ?.filter(({ feeType }) => !feeType || keepTransferFeeAsPayment)
            .map(({ instrumentId, direction, quantity: units }) => {
              return {
                instrumentId,
                direction,
                quantity: {
                  instrumentId,
                  quantity: direction === 'sent' ? -units : units,
                },
              };
            }),
        }),
      ),
      instruments: event?.instruments?.map?.(
        ({ nameFromDraft, instrumentId, unitsTotal, unitsDelta }) => {
          return {
            nameFromDraft,
            instrumentId,
            total: {
              instrumentId,
              quantity: unitsTotal,
            },
            delta: {
              instrumentId,
              quantity: unitsDelta,
            },
          };
        },
      ),
      fees,
    };

    if (mappedEvent.transactions) {
      mappedEvent.transactions = mappedEvent.transactions.reduce(
        (deduplicated, nextTransaction) => {
          let wasAlreadyAdded = false;
          const merged = deduplicated.map((transaction) => {
            if (this.isSameTransaction(transaction, nextTransaction)) {
              wasAlreadyAdded = true;
              if (
                nextTransaction.pendingTransactionId ||
                nextTransaction.txId
              ) {
                return nextTransaction;
              }
            }
            return transaction;
          });
          if (!wasAlreadyAdded) {
            merged.push(nextTransaction);
          }
          return merged;
        },
        [],
      );
    }

    switch (mappedEvent?.activityEventType) {
      case 'lock_box_creation_proposal':
        return this.transformLockboxCreate(mappedEvent, context);
      case 'payment':
      case 'transfer_fee':
      case 'dust':
        return this.transformInstrumentSend(mappedEvent, context);
      case 'trade_offer':
        if (mappedEvent?.counterparties?.[0]?.transfers?.length === 1) {
          return this.transformInstrumentRequest(mappedEvent, context);
        }
        return this.transformInstrumentTrade(mappedEvent, context);
      case 'contract_formation':
        return this.transformContractCreate(mappedEvent, context);
      case 'contract_amendment':
        return this.transformContractAmend(mappedEvent, context);
      case 'contract_expire':
        return this.transformContractExpire(mappedEvent, context);
      case 'instrument_expire':
        return this.transformInstrumentExpire(mappedEvent, context);
      case 'reorg':
        return this.transformReorg(mappedEvent, context);
      default:
        return mappedEvent;
    }
  }

  instrumentsNeededForEventPreparation(event) {
    const instrumentIds = new Set();

    event?.counterparties?.forEach?.((party) => {
      party.transfers?.forEach?.((transfer) => {
        if (transfer.instrumentId) {
          instrumentIds.add(transfer.instrumentId);
        }
      });
    });
    event?.instruments?.forEach?.((eventInstrument) => {
      if (eventInstrument.instrumentId) {
        instrumentIds.add(eventInstrument.instrumentId);
      }
    });
    event?.fees?.breakdown?.forEach?.(({ instrumentId }) => {
      instrumentIds.add(instrumentId);
    });

    return instrumentIds;
  }

  handlesNeededForEventPreparation(event) {
    const handles = new Set();

    event?.timeline?.forEach((handle) => {
      if (handle) {
        handles.add(handle);
      }
    });

    return handles;
  }

  prepareEventForRender({
    event,
    intl,
    instrumentsById,
    contractsById,
    entityId,
  } = {}) {
    if (!event?.activityEventType) {
      return event;
    }

    // Sometimes an instrument create/amend event has no contract info,
    // but we can still get it via the instrument data
    let completedContract = event?.contract;
    let completedInstruments = event?.instruments;
    let formatContractIssuer;
    let formatByContractIssuer;
    switch (event?.activityEventType) {
      case 'contract_formation':
      case 'contract_amendment':
      case 'contract_expire':
      case 'instrument_expire': {
        const firstInstrument = instrumentsById?.get?.(
          event.instruments?.[0]?.instrumentId,
        );
        const contractInfo = contractsById?.get?.(
          event.contract?.contractAddress || firstInstrument?.contractAddress,
        );

        const contractIssuerInfo = contractInfo?.issuerHandle
          ? {
              displayHandle: contractInfo?.issuerHandle,
              displayName: contractInfo?.issuer?.name,
              isInternal: true,
              isVerified: true,
            }
          : undefined;
        formatContractIssuer =
          contractIssuerInfo &&
          this.makeFormatEntityDescription(contractIssuerInfo, null);
        formatByContractIssuer =
          contractIssuerInfo &&
          this.makeFormatEntityDescription(contractIssuerInfo, 'by');

        if (!completedInstruments && contractInfo?.instruments) {
          completedInstruments = contractInfo.instruments.map(
            (contractInstrumentInfo) => {
              const instrumentId =
                contractInstrumentInfo.instrumentId ||
                contractInstrumentInfo.draft?.id;
              return {
                instrumentId,
                total: {
                  instrumentId,
                  quantity: contractInstrumentInfo.authorizedQuantity,
                },
                formatted: {
                  formatDescription: this.makeFormatTransferDescription({
                    nameFromDraft:
                      contractInstrumentInfo.formatted?.formatName?.({ intl }),
                  }),
                },
              };
            },
          );
        }

        const contractAddress =
          event.contract?.contractAddress || contractInfo?.contractAddress;
        const contractName = event.contract?.name || contractInfo?.name;

        completedContract = {
          ...event?.contract,
          contractAddress,
          draftId: contractInfo?.draft?.id,
          name: contractName,
          issuerHandle:
            event.contract?.issuerHandle || contractInfo?.issuerHandle,
          contractType: contractInfo?.contractType,
          formatted: {
            ...event.contract?.formatted,
            description: event.contract?.description || contractName,
            descriptionParts:
              event.contract?.descriptionParts ||
              (contractName
                ? [
                    {
                      type: 'contract',
                      contractAddress,
                      value: contractName,
                    },
                  ]
                : undefined),
            type: contractInfo?.formatted?.formatType?.(intl),
            instrumentSummary:
              contractInfo?.formatted?.formatInstrumentSummary?.({
                intl,
                count: completedInstruments?.length,
              }),
            issuer: formatContractIssuer?.(intl, false),
            issuerParts: formatContractIssuer?.(intl, true),
            actionFee: contractInfo?.formatted?.actionFee,
          },
        };
        break;
      }
      default:
        break;
    }

    const transferInstrumentIds = new Set();

    const preparedEvent = {
      ...event,
      lockbox: event.lockbox && {
        ...event.lockbox,
        formatted: {
          ...event.lockbox.formatted,
          controlArrangement:
            event.lockbox.formatted?.formatControlArrangement?.(intl),
        },
      },
      formerLockboxes: event.formerLockboxes?.map((lockbox) => ({
        ...lockbox,
        formatted: {
          ...lockbox.formatted,
          controlArrangement:
            lockbox.formatted?.formatControlArrangement?.(intl),
        },
      })),
      counterparties: event.counterparties?.map?.((party) => ({
        ...party,
        formatted: { descriptionParts: party.format?.(intl, true) },
        transfers: party.transfers?.map?.((transfer) => {
          if (transfer.instrumentId) {
            transferInstrumentIds.add(transfer.instrumentId);
          }
          const transferInstrument = instrumentsById?.get?.(
            transfer.instrumentId,
          );
          return {
            ...transfer,
            instrument: transferInstrument,
            quantity:
              transferInstrument && transfer.quantity
                ? transferInstrument.withQuantity(transfer.quantity)
                : transfer.quantity,
            formatted: {
              description: transfer.format?.(
                intl,
                instrumentsById?.get?.(transfer.instrumentId),
                false,
              ),
              descriptionParts: transfer.format?.(
                intl,
                instrumentsById?.get?.(transfer.instrumentId),
                true,
              ),
            },
          };
        }),
      })),
      instruments: completedInstruments?.map?.((eventInstrument) => {
        const instrument = instrumentsById?.get?.(eventInstrument.instrumentId);
        return {
          ...eventInstrument,
          instrument,
          total:
            instrument && eventInstrument.total
              ? instrument.withQuantity(eventInstrument.total)
              : eventInstrument.total,
          delta:
            instrument && eventInstrument.delta
              ? instrument.withQuantity(eventInstrument.delta)
              : eventInstrument.delta,
          formatted: {
            ...eventInstrument.formatted,
            description: eventInstrument.formatted?.formatDescription?.(
              intl,
              instrument,
              false,
            ),
            descriptionParts: eventInstrument.formatted?.formatDescription?.(
              intl,
              instrument,
              true,
            ),
          },
        };
      }),
      contract: completedContract,
      fees: Object.entries(event.fees || {})
        .map(([key, value]) => {
          if (key === 'breakdown') {
            return [
              key,
              value.map(({ type, instrumentId, quantity }) => ({
                type,
                transfer:
                  instrumentsById
                    ?.get(instrumentId)
                    ?.withQuantity({ quantity }) ||
                  new Instrument({ instrumentId, quantity }),
              })),
            ];
          }
          return [
            key,
            instrumentsById?.get(value.instrumentId)?.withQuantity(value) ||
              new Instrument({
                instrumentId: value.instrumentId,
                quantity: value.quantity,
              }),
          ];
        })
        .reduce((prev, [key, value]) => ({ ...prev, [key]: value }), {}),
      timeline:
        event.timeline &&
        event.timeline.map((timelineEvent) => ({
          ...timelineEvent,
          formatDescription:
            timelineEvent.formatDescription &&
            ((options) =>
              timelineEvent.formatDescription({ intl, ...options })),
        })),
    };

    switch (preparedEvent.activityEventType) {
      case 'lock_box_creation_proposal':
      case 'reorg':
        return {
          ...preparedEvent,
          formatted: {
            ...preparedEvent.formatted,
            description: preparedEvent.formatted?.formatDescription?.(intl),
            counterparty: preparedEvent.formatted?.formatCounterparty?.(
              intl,
              false,
            ),
            counterpartyParts: preparedEvent.formatted?.formatCounterparty?.(
              intl,
              true,
            ),
            role: preparedEvent.role?.format?.(intl),
          },
          entity: {
            ...preparedEvent.entity,
            formatted: {
              descriptionParts: preparedEvent.entity?.format?.(intl, true),
            },
          },
        };
      case 'payment':
      case 'transfer_fee':
      case 'dust': {
        const isLiability = preparedEvent.instruments?.some(
          (eventInstrument) =>
            transferInstrumentIds.has(eventInstrument.instrumentId) &&
            eventInstrument.instrument?.isLiabilityForEntity?.(entityId),
        );
        let feePayingInstrument;
        if (preparedEvent.feePayingInstrumentId) {
          feePayingInstrument = instrumentsById?.get?.(
            preparedEvent.feePayingInstrumentId,
          );
        }
        return {
          ...preparedEvent,
          formatted: {
            ...preparedEvent.formatted,
            description: preparedEvent.formatted?.formatDescription?.(intl, {
              isLiability,
            }),
            counterparty: preparedEvent.formatted?.formatCounterparty?.(
              intl,
              false,
            ),
            ...(feePayingInstrument
              ? {
                  feePayingInstrument:
                    preparedEvent.formatted.formatFeePayingInstrument(
                      intl,
                      feePayingInstrument,
                      false,
                    ),
                  feePayingInstrumentParts:
                    preparedEvent.formatted.formatFeePayingInstrument(
                      intl,
                      feePayingInstrument,
                      true,
                    ),
                }
              : {}),
          },
          ...(feePayingInstrument ? { feePayingInstrument } : {}),
        };
      }
      case 'trade_offer': {
        if (preparedEvent?.isTransferRequest) {
          const instrumentReceive =
            preparedEvent.counterparties?.[0]?.transfers?.[0]?.instrument;
          return {
            ...preparedEvent,
            formatted: {
              ...preparedEvent.formatted,
              description: preparedEvent.formatted?.formatDescription?.(intl),
              counterparty: preparedEvent.formatted?.formatCounterparty?.(
                intl,
                false,
              ),
              responseInstrument:
                preparedEvent.formatted?.formatResponseInstrument?.(
                  intl,
                  instrumentReceive,
                  false,
                ),
            },
          };
        }
        const transferReceive =
          preparedEvent?.counterparties?.[0]?.transfers?.find?.(
            (transfer) => transfer.direction === 'received',
          );
        const transferSend =
          preparedEvent?.counterparties?.[0]?.transfers?.find?.(
            (transfer) => transfer.direction === 'sent',
          );
        return {
          ...preparedEvent,
          formatted: {
            ...preparedEvent.formatted,
            description: preparedEvent.formatted?.formatDescription?.(intl),
            counterparty: preparedEvent.formatted?.formatCounterparty?.(
              intl,
              false,
            ),
            tradeAction: preparedEvent.formatted?.formatTradeAction?.(
              intl,
              transferSend?.instrument,
              false,
            ),
            tradeResponse: preparedEvent.formatted?.formatTradeResponse?.(
              intl,
              transferReceive?.instrument,
              false,
            ),
          },
        };
      }
      case 'contract_formation':
      case 'contract_amendment':
        return {
          ...preparedEvent,
          formatted: {
            ...preparedEvent.formatted,
            description: preparedEvent.formatted?.formatDescription?.(intl),
            initiator: formatByContractIssuer?.(intl, false),
            initiatorParts: formatByContractIssuer?.(intl, true),
          },
        };
      case 'contract_expire':
      case 'instrument_expire':
        return {
          ...preparedEvent,
          formatted: {
            ...preparedEvent.formatted,
            description: preparedEvent.formatted?.formatDescription?.(intl),
          },
        };
      default:
        return preparedEvent;
    }
  }
}
